import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import DocumentAppBar from "./components/DocumentAppBar";
import DocumentHeader from "./components/DocumentHeader";
import RevisionTable from "./components/RevisionTable";
import { useParams, useLocation } from "react-router-dom";
import TrainingLogTable from "./components/TrainingLogTable";
import BOMTable from "./components/BOMTable";
import RenderRevisionNodes from "./components/RenderRevisionNodes";
import { HIDE_CHILD_HEADING_TYPES_TRAINING_CHECKMARK } from "./components/Nodes/ChildrenRenderer";
import useDocumentData from "./hooks/useDocumentData";
import useCurrentRevisionData from "./hooks/useCurrentRevisionData";
import InlineLoader from "../../common/InlineLoader";
import { useSelector, useDispatch } from "react-redux";
import {
  extractVersionHistoryResults,
  getAllVersionHistories,
  versionHistoryRowDedupeKey,
} from "../../redux/versionHistorySlice";
import {
  normalizeVersionIdRef,
  uniqueVersionIdRefs,
} from "../../util/versionIdRefUtils";
import VersionHistoryPopup from "../../common/VersionHistoryPopup/VersionHistoryPopup";
import { getVersionHistoryAuditPreviewPath } from "../../common/VersionHistoryPopup/getVersionHistoryAuditPreviewPath";
import { getVersionIdsByLtreePath } from "../../redux/procedureRevisionSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  getGlobalAppViewMode,
  setGlobalAppViewMode,
} from "../../util/appViewModeStorage";
import { buildPermissionContext, PERMISSIONS } from "../../util/permissions";
import { useNavigate } from "react-router-dom";
import { isLocked } from "./Utils/utils.js";
import {
  getAllLockStatus,
  getRevisionLockStatus,
  pingTask,
  lockTask,
  unlockTask,
} from "../../util/apiService";
import { showSnackbar } from "../../redux/snackbarSlice";
import useApiTabLock from "../../hooks/useApiTabLock";
import { useRevisionLock } from "../../contexts/RevisionLockContext";
import { ModalActions } from "../../common/Buttons";
import "../../common/Modals/UnifiedRevisionModal/UnifiedRevisionModal.css";
import CompleteTrainingModal from "./components/CompleteTrainingModal";
import SelectTraineesModal from "./components/SelectTraineesModal";
import {
  createTrainingLog,
  updateTrainingLog,
  getTrainingLogsForProcedureRevision,
} from "../../redux/trainingLogSlice";
import {
  buildCreateTrainingLogTreeNodeIdArray,
  buildTrainedToPayload,
  resolveTrainedToOuterTreeNodeId,
} from "./Utils/trainingLogPayload";
import { getAppTimezone } from "../../util/timezoneUtils";
import { getCurrentStatus } from "../../util/AppUtils.jsx";
import { getAuditTrailData } from "../../util/getAuditTrailData";
import {
  buildInheritedFullyTrainedTraineeIds,
  findLatestReleasedProcedureRevision,
  resolveRevisionRevLabelFromContext,
  derivePerStepTrainingSetsFromLog,
  fetchLatestTrainingLogForProcedureRevisionForTrainee,
  fetchTrainingStatusLabelForProcedureRevisionForTrainee,
  isRevisionWorkflowActor,
  collectWorkflowActorUserIdsFromSameLetterRevisions,
} from "../../util/trainingLogStatus";
import { updatePartsRequiredQty } from "../../redux/stepSlice";
import {
  fetchGiveTrainingTraineeUsers,
  clearGiveTrainingTraineeUsers,
  fetchSelectTrainerUsers,
  clearSelectTrainerUsers,
} from "../../redux/userSlice";

/** Loose UUID shape for user refs (avoids `trainer_id: false` → `"false"` in sub_aprr_rel_users). */
const TRAINING_LOG_USER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeTrainingLogUserId(raw) {
  if (raw == null || typeof raw === "boolean") return null;
  const s = String(raw).trim();
  if (s === "" || s === "false" || s === "true") return null;
  return TRAINING_LOG_USER_ID_RE.test(s) ? s : null;
}

/** Trainer id on a training-log row (string or embedded object). Booleans on trainer_id are ignored. */
function extractTrainerIdFromTrainingLogRow(row) {
  if (!row || typeof row !== "object") return null;
  const t = row.trainer;
  if (t != null && typeof t === "object") {
    const v = t.id ?? t.user_id ?? t.user_ref;
    const n = normalizeTrainingLogUserId(v);
    if (n) return n;
  }
  if (typeof t === "string") {
    const n = normalizeTrainingLogUserId(t);
    if (n) return n;
  }
  const fromFields = row.trainer_id ?? row.trainer_ref;
  return normalizeTrainingLogUserId(fromFields);
}

/** Unique trainer ids from training-log rows with `part_number === null` → `sub_aprr_rel_users` on user/get-all. */
function buildSubAprRelUserIdsFromTrainingLogResults(results) {
  const list = Array.isArray(results) ? results : [];
  const out = [];
  const seen = new Set();
  for (const row of list) {
    if (row == null || typeof row !== "object") continue;
    if (row.part_number !== null) continue;
    const tid = extractTrainerIdFromTrainingLogRow(row);
    if (tid == null || seen.has(tid)) continue;
    seen.add(tid);
    out.push(tid);
  }
  return out;
}

/** Merge UUID strings for Give Training `sub_aprr_rel_users` (dedupe, normalized lowercase). */
function mergeGiveTrainingSubAprRelUserIds(logIds, revisionSameLetterIds) {
  const seen = new Set();
  const out = [];
  const push = (raw) => {
    const s = String(raw ?? "").trim().toLowerCase();
    if (!TRAINING_LOG_USER_ID_RE.test(s)) return;
    if (seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  for (const id of logIds || []) push(id);
  for (const id of revisionSameLetterIds || []) push(id);
  return out;
}

/** Select Trainer / Trainee modals: max users per vertical column before adding a new column. */
const SELECT_MODAL_USERS_PER_COLUMN = 8;

function chunkUserOptionsIntoColumns(options, perColumn = SELECT_MODAL_USERS_PER_COLUMN) {
  const cols = [];
  const list = Array.isArray(options) ? options : [];
  for (let i = 0; i < list.length; i += perColumn) {
    cols.push(list.slice(i, i + perColumn));
  }
  return cols;
}

/** Select the Trainer: include if true. Select Trainee(s): exclude if true (same condition, inverted). */
function userIsTrainingDoneOrTrainerForSubAprRel(u) {
  if (!u || typeof u !== "object") return false;
  return (
    u.is_training_completed === true ||
    u.is_trainned_for_sub_aprr_rel === true
  );
}

/**
 * Document Component
 * Main document page that displays procedure revision information
 * Composed of multiple sub-components with data fetching handled by custom hooks
 */
function Document() {
  const dispatch = useDispatch();
  const location = useLocation();
  // When navigated from audit trail (e.g. Cancel on preview), hide tables dropdown, type in title, action buttons, etc.
  // Use sessionStorage so it persists if location.state is lost during navigation
  const fromAuditTrail =
    location.state?.fromAuditTrail === true ||
    sessionStorage.getItem("fromAuditTrail") === "true";

  const { cancel_screen_data } = location?.state || {}

  /** ISO timestamp of the audit/version row when opening from audit-trail preview cancel (for AppBar subtitle). */
  const auditTrailSavedAtIso = useMemo(() => {
    if (!fromAuditTrail) return null;
    const fromState = location.state?.auditTrailSavedAtIso;
    if (fromState) return String(fromState);
    try {
      return sessionStorage.getItem("auditTrailSavedAtIso") || null;
    } catch {
      return null;
    }
  }, [fromAuditTrail, location.state?.auditTrailSavedAtIso]);

  /** S3 URL for audit `new_content_snapshot` — tree shown on revision-submission after preview Cancel. */
  const auditTrailNewContentSnapshotUrl = useMemo(() => {
    if (!fromAuditTrail) return null;
    const fromState = location.state?.auditTrailNewContentSnapshotUrl;
    if (fromState) return String(fromState);
    try {
      return sessionStorage.getItem("auditTrailNewContentSnapshotUrl") || null;
    } catch {
      return null;
    }
  }, [fromAuditTrail, location.state?.auditTrailNewContentSnapshotUrl]);

  // Clear audit-trail flag when leaving this page so future visits to revision-submission are normal
  useEffect(() => {
    return () => {
      sessionStorage.removeItem("fromAuditTrail");
      sessionStorage.removeItem("auditTrailBackAuditTrailId");
      sessionStorage.removeItem("auditTrailSavedAtIso");
      sessionStorage.removeItem("auditTrailNewContentSnapshotUrl");
    };
  }, []);

  // Extract URL parameters
  const { partId, revisionId } = useParams();

  // Store all nodes data for caching in preview
  const [allNodesData, setAllNodesData] = useState(null);

  // Refresh key to force re-fetch of nodes data when returning to page
  const [nodesRefreshKey, setNodesRefreshKey] = useState(0);

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showAdjustQtyPopup, setShowAdjustQtyPopup] = useState(false);
  const [adjustQtyValue, setAdjustQtyValue] = useState("");
  const [adjustQtySubmitting, setAdjustQtySubmitting] = useState(false);
  const [totalQtyToBuild, setTotalQtyToBuild] = useState(null);
  const totalQtyToBuildStorageKey = useMemo(() => {
    if (!revisionId) return null;
    return `bom_total_qty_to_build_${String(revisionId).trim().toLowerCase()}`;
  }, [revisionId]);

  // Persist "Adjust QTY" across navigation/refresh (per revision).
  useEffect(() => {
    if (!totalQtyToBuildStorageKey) return;
    try {
      const raw = localStorage.getItem(totalQtyToBuildStorageKey);
      if (raw == null || raw === "") return;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed < 0) return;
      setTotalQtyToBuild(parsed);
    } catch {
      /* ignore */
    }
  }, [totalQtyToBuildStorageKey]);

  // When leaving this revision screen (back / navigate away), clear the stored adjustment.
  // This keeps it persistent only for refreshes while you stay on the same page.
  useEffect(() => {
    if (!totalQtyToBuildStorageKey) return;
    return () => {
      try {
        localStorage.removeItem(totalQtyToBuildStorageKey);
      } catch {
        /* ignore */
      }
    };
  }, [totalQtyToBuildStorageKey]);
  const [showSelectTrainerPopup, setShowSelectTrainerPopup] = useState(false);
  /** Get Training → Select the Trainer: loading training logs + user/get-all. */
  const [selectTrainerModalBusy, setSelectTrainerModalBusy] = useState(false);
  /** Give Training — separate from Get Training (Select the Trainer). */
  const [showSelectTraineePopup, setShowSelectTraineePopup] = useState(false);
  const [giveTrainingFlow, setGiveTrainingFlow] = useState(false);
  const [giveTrainingSelectedTraineeIds, setGiveTrainingSelectedTraineeIds] =
    useState([]);
  /**
   * Give Training: `{ id, label }[]` from Select Trainee(s) Next — survives `clearGiveTrainingTraineeUsers()`.
   */
  const [giveTrainingSessionTraineeEntries, setGiveTrainingSessionTraineeEntries] =
    useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState("");
  /**
   * Get Training: set when user clicks Next on Select the Trainer. Closing the modal clears Redux
   * and `selectedTrainer`, but Complete Training still needs the chosen trainer id + display name.
   */
  const [getTrainingSessionTrainerId, setGetTrainingSessionTrainerId] =
    useState("");
  const [getTrainingSessionTrainerLabel, setGetTrainingSessionTrainerLabel] =
    useState("");
  /** After Select Trainer → Next: training app bar (view mode only). */
  const [trainingCheckmarkMode, setTrainingCheckmarkMode] = useState(false);
  /** Procedure vs step: select-only (no navigation) while in training checkmark flow. */
  const [trainingCheckmarkSelection, setTrainingCheckmarkSelection] =
    useState(null);
  /** Steps already trained (used by "Checkmark Where Trained" screen). */
  const [trainedStepNumbersSet, setTrainedStepNumbersSet] = useState(null);
  /** Steps with "Partially Trained" in latest log `trained_to` (global step numbers). */
  const [partiallyTrainedStepNumbersSet, setPartiallyTrainedStepNumbersSet] =
    useState(null);
  const [allStepsTrained, setAllStepsTrained] = useState(false);
  /** Trained steps as step tree_node_ids (used to pre-check checkboxes). */
  const [trainedStepTreeNodeIdsSet, setTrainedStepTreeNodeIdsSet] =
    useState(null);
  /** Multi-trainee completion: queue of trainee ids for sequential popups. */
  const [completeTrainingTraineeIds, setCompleteTrainingTraineeIds] = useState(
    [],
  );
  const [completeTrainingTraineeIndex, setCompleteTrainingTraineeIndex] =
    useState(0);
  const [showCompleteTrainingModal, setShowCompleteTrainingModal] =
    useState(false);
  const [showRevisionTableSection, setShowRevisionTableSection] =
    useState(false);
  const [showTrainingLogSection, setShowTrainingLogSection] = useState(false);
  /** Increment after a training log is saved so DocumentAppBar refetches status immediately */
  const [trainingStatusRefreshKey, setTrainingStatusRefreshKey] = useState(0);
  /**
   * Aggregate label from GET training-log (same as app bar). When "Trained", only Give Training is
   * offered on the main view; per-step tags show again on the Give Training checkmark screen after
   * trainees are selected (see showStepTrainingTags).
   */
  const [viewerAggregateTrainingStatusLabel, setViewerAggregateTrainingStatusLabel] =
    useState(null);

  // Ref to access RevisionTable's create revision function
  const revisionTableRef = useRef();

  // Use revision ID as route key to lock entire revision across all pages
  // This ensures all pages within the same revision (Document, sections, steps, substeps, canvases, etc.)
  // share the same lock across tabs
  // IMPORTANT: Use consistent routeKey across all revision-related pages
  const routeKeyRef = useRef(`revision_${revisionId}`);
  const [routeKey, setRouteKey] = useState(`revision_${revisionId}`);

  // State to track if we should temporarily bypass tab order lock
  // Initialize by checking if THIS specific tab has a bypass flag
  const [bypassTabOrderLock, setBypassTabOrderLock] = useState(() => {
    const tabId = sessionStorage.getItem("tab-id");
    const bypassFlag = sessionStorage.getItem(
      `tabOrderLockBypass_${revisionId}_${tabId}`,
    );
    return bypassFlag === "true";
  });

  // Lock status state
  const [lockStatus, setLockStatus] = useState(null);
  const [lockErrorMessage, setLockErrorMessage] = useState(null);
  const [revisionLockStatus, setRevisionLockStatus] = useState(null);
  const [isRevisionLocked, setIsRevisionLocked] = useState(false);

  // Tab lock management - only one tab can perform actions
  // Use revision-specific key to ensure all pages within the same revision share the same lock
  const {
    isLocked: isApiLocked,
    isActiveTab,
    lockedBy,
  } = useApiTabLock(`revision_${revisionId}`);

  // Get auth user and check permissions - moved here to be used in useEffect
  const authUser = useSelector((state) => state.auth.user);
  const appSetup = useSelector((state) => state.appSetup);

  // Tab order lock from provider (lock held at app level so it is NOT released when navigating to view-revision)
  const revisionLock = useRevisionLock();
  const revisionLockMatchesRoute =
    revisionLock?.revisionIdFromPath != null &&
    revisionId != null &&
    String(revisionLock.revisionIdFromPath).trim().toLowerCase() ===
      String(revisionId).trim().toLowerCase();
  const isFirstTabByOrder = revisionLockMatchesRoute
    ? revisionLock.isFirstTab
    : false;
  const isLockedByOrder = revisionLockMatchesRoute
    ? revisionLock.isLocked
    : true;

  // DISABLED: Auto-bypass mechanism
  // The Web Locks API handles leader election automatically.
  // Tabs should remain locked until the leader tab closes.
  // Auto-bypass is only used for returning from navigation, not for bypassing legitimate locks.
  useEffect(() => {
    const tabId = sessionStorage.getItem("tab-id");

    // Only monitor for cleanup - don't auto-bypass after timeout
    // The Web Locks API will automatically promote Tab 2 to leader when Tab 1 closes
    let healthyCount = 0;

    const checkTabOrderLockHealth = () => {
      if (!isLockedByOrder) {
        // Lock is healthy (this tab is the leader), increment healthy counter
        healthyCount++;

        // Only consider lock truly healthy after 5 consecutive healthy checks
        // BUT don't remove bypass if we're returning from a node view (to prevent race conditions)
        const returningFromNodeView =
          sessionStorage.getItem(`returningFromViewRevision_${revisionId}`) ===
          "true";

        if (healthyCount > 5 && !returningFromNodeView) {
          if (bypassTabOrderLock) {
            setBypassTabOrderLock(false);
            sessionStorage.removeItem(
              `tabOrderLockBypass_${revisionId}_${tabId}`,
            );
          }
        }
      } else {
        // Lock is held by another tab - this is normal, just reset healthy counter
        healthyCount = 0;
      }
    };

    // Check every 2 seconds (less frequent since we're not doing auto-bypass)
    const interval = setInterval(checkTabOrderLockHealth, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [isLockedByOrder, bypassTabOrderLock, revisionId]);

  // Track if this tab was the leader before navigating away
  useEffect(() => {
    const tabId = sessionStorage.getItem("tab-id");

    // Check if we're returning from a node view
    const returningFromNodeView =
      sessionStorage.getItem(`returningFromViewRevision_${revisionId}`) ===
      "true";

    // Check if this tab was previously the leader for this revision
    const wasLeader =
      sessionStorage.getItem(`wasLeader_${revisionId}_${tabId}`) === "true";

    if (returningFromNodeView && wasLeader) {
      // If we were the leader before and are returning, enable bypass immediately
      // This prevents other tabs from stealing the lock during the Web Locks race condition
      setBypassTabOrderLock(true);
      // Use tab-specific bypass flag so it doesn't affect other tabs
      sessionStorage.setItem(
        `tabOrderLockBypass_${revisionId}_${tabId}`,
        "true",
      );
      return;
    }
  }, [revisionId]);

  // Track when this tab becomes the leader
  useEffect(() => {
    const tabId = sessionStorage.getItem("tab-id");

    if (isFirstTabByOrder) {
      sessionStorage.setItem(`wasLeader_${revisionId}_${tabId}`, "true");
    }
  }, [isFirstTabByOrder, revisionId]);

  // Listen for navigation back from view-revision and refresh lock status
  useEffect(() => {
    const handleNavigationFromViewRevision = async (event) => {
      if (
        event.detail?.revisionId === revisionId ||
        event.detail?.revision_id === revisionId
      ) {
        try {
          const status = await getRevisionLockStatus(revisionId);
          setLockStatus(status);
        } catch (error) {
          // Failed to refresh lock status from event
        }
      }
    };

    window.addEventListener(
      "navigation-from-view-revision",
      handleNavigationFromViewRevision,
    );

    return () => {
      window.removeEventListener(
        "navigation-from-view-revision",
        handleNavigationFromViewRevision,
      );
    };
  }, [revisionId, authUser, isFirstTabByOrder]);

  // Store the tab order lock release function
  const tabOrderLockRef = useRef(null);

  // Create a ref to hold the current tab order lock state for cleanup
  useEffect(() => {
    // This effect will track the tab order lock and provide cleanup
    return () => {
      // Cleanup will be handled by the useTabOrderLock hook itself
    };
  }, [routeKey]);

  // Additional information container state
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  // Get version histories from Redux
  const { versionHistories } = useSelector((state) => state.versionHistory);

  // Get app setup data from Redux
  const { items: appSetupItems } = useSelector((state) => state.appSetup || {});

  // Check permissions using the already declared authUser
  const permissionCtx = buildPermissionContext(authUser);
  const canView = permissionCtx.permissions?.[PERMISSIONS.VIEW] === true;
  const canEdit = permissionCtx.permissions?.[PERMISSIONS.EDIT] === true;
  const canCreate = permissionCtx.permissions?.[PERMISSIONS.CREATE] === true;
  const canClick = canView || canEdit;

  const navigate = useNavigate();

  const documentViewModeStorageKey = revisionId
    ? `document_is_view_mode_${revisionId}`
    : "document_is_view_mode";

  const readIsViewModeFromStorage = useCallback(() => {
    const global = getGlobalAppViewMode();
    if (global !== null) return global;
    const perRev = sessionStorage.getItem(documentViewModeStorageKey);
    if (perRev === "true" || perRev === "false") return perRev === "true";
    return false;
  }, [documentViewModeStorageKey, revisionId]);

  const isViewMode = useMemo(() => {
    const raw = location.state?.isViewMode;
    if (typeof raw === "boolean") return raw;
    if (raw === "true") return true;
    if (raw === "false") return false;
    return readIsViewModeFromStorage();
  }, [location.state?.isViewMode, readIsViewModeFromStorage]);

  useEffect(() => {
    const raw = location.state?.isViewMode;
    if (typeof raw === "boolean") {
      const v = String(raw);
      sessionStorage.setItem(documentViewModeStorageKey, v);
      setGlobalAppViewMode(raw);
    } else if (raw === "true" || raw === "false") {
      sessionStorage.setItem(documentViewModeStorageKey, raw);
      setGlobalAppViewMode(raw === "true");
    }
  }, [location.state?.isViewMode, documentViewModeStorageKey]);

  /** Preserved when opening a revision from Add Module (see DocumentAppBar back navigation). */
  const fromAddModule = useMemo(() => {
    if (location.state?.fromAddModule === true) return true;
    try {
      const storageKey = `navFromAddModule_${partId}_${revisionId}`;
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.fromAddModule === true;
      }
    } catch {
      /* ignore */
    }
    return false;
  }, [location.state?.fromAddModule, partId, revisionId]);

  // Fetch all document data using custom hook
  const {
    revisionData,
    trainingLogs,
    users,
    approvedUsers,
    releasedUsers,
    rule,
    bomTable,
    loading,
    refreshRevisionData,
    refreshBomTable,
  } = useDocumentData(partId, revisionId,false,{
    fromAuditTrail,
    present_revisions:cancel_screen_data?.present_revisions
  });

  const giveTrainingTraineeUsers = useSelector(
    (state) => state.users.giveTrainingTraineeUsers,
  );
  const giveTrainingTraineeUsersLoading = useSelector(
    (state) => state.users.giveTrainingTraineeUsersLoading,
  );
  const selectTrainerUsers = useSelector(
    (state) => state.users.selectTrainerUsers,
  );

  // Get current revision and status using custom hook
  const { currentRevision, currentStatus } = useCurrentRevisionData(
    revisionData,
    revisionId,
  );

  /**
   * When version history API rows lack breadcrumb_text, prepend item code + rev so
   * Version ID shows e.g. "13310 > Rev A- > … > Version {id}" instead of "> Version {id}".
   */
  const versionHistoryBreadcrumbFallback = useMemo(() => {
    if (fromAuditTrail && cancel_screen_data?.partNumber) {
      const pn = String(cancel_screen_data.partNumber).trim();
      const r = cancel_screen_data.revision;
      const revPart =
        r != null && String(r).trim() !== ""
          ? `Rev ${String(r).trim()}`
          : "";
      return [pn, revPart].filter(Boolean).join(" > ");
    }
    const pn = currentRevision?.part_number;
    const r = currentRevision?.rev;
    if (!pn && (r == null || String(r).trim() === "")) {
      return "";
    }
    const code = pn != null ? String(pn).trim() : "";
    const revPart =
      r != null && String(r).trim() !== ""
        ? `Rev ${String(r).trim()}`
        : "";
    return [code, revPart].filter(Boolean).join(" > ");
  }, [
    currentRevision?.part_number,
    currentRevision?.rev,
    fromAuditTrail,
    cancel_screen_data?.partNumber,
    cancel_screen_data?.revision,
  ]);

  /** Popup title, e.g. "Version History for 13310 Rev C-" (matches reference layout). */
  const versionHistoryPopupTitle = useMemo(() => {
    if (fromAuditTrail && cancel_screen_data?.partNumber) {
      const pn = String(cancel_screen_data.partNumber).trim();
      const r = cancel_screen_data.revision;
      if (pn && r != null && String(r).trim() !== "") {
        return `Version History for ${pn} Rev ${String(r).trim()}`;
      }
      if (pn) return `Version History for ${pn}`;
    }
    const pn = currentRevision?.part_number;
    const r = currentRevision?.rev;
    if (pn != null && String(pn).trim() !== "" && r != null && String(r).trim() !== "") {
      return `Version History for ${String(pn).trim()} Rev ${String(r).trim()}`;
    }
    if (pn != null && String(pn).trim() !== "") {
      return `Version History for ${String(pn).trim()}`;
    }
    return "Version History";
  }, [
    currentRevision?.part_number,
    currentRevision?.rev,
    fromAuditTrail,
    cancel_screen_data?.partNumber,
    cancel_screen_data?.revision,
  ]);

  /** Live (view) mode is only valid for Released revisions; Edit→Live otherwise goes to 404. */
  const isReleasedRevision = useMemo(() => {
    const s = String(
      currentStatus ?? getCurrentStatus(currentRevision) ?? "",
    )
      .trim()
      .toLowerCase();
    return s === "released";
  }, [currentStatus, currentRevision]);

  /** Get Training → Select Trainer: `part_ref` on user/get-all must be the route part id (`part_id`). */
  const partRefForSelectTrainer = useMemo(() => {
    if (partId != null && String(partId).trim() !== "") {
      return String(partId).trim();
    }
    return "";
  }, [partId]);

  /** Older revisions (Revisions table order, including e.g. A1 → B-): fully trained trainees inherit "Trained". */
  const inheritedTrainedTraineeIdsForCurrentRevision = useMemo(() => {
    const letterContextRev = resolveRevisionRevLabelFromContext(
      revisionData,
      revisionId,
      currentRevision?.rev,
    );
    const eff = findLatestReleasedProcedureRevision(
      revisionData,
      letterContextRev,
    );
    return buildInheritedFullyTrainedTraineeIds({
      trainingLogs,
      revisionData,
      currentProcedureRevisionId: eff?.procedureRevisionId ?? revisionId,
      currentRev: eff?.rev ?? currentRevision?.rev,
    });
  }, [trainingLogs, revisionData, revisionId, currentRevision?.rev]);

  /** Viewer GET training: treat as Trained when a prior revision (any letter, per revision order) was fully trained. */
  const viewerTrainingStatusWithLetterInheritance = useMemo(() => {
    const base = viewerAggregateTrainingStatusLabel;
    const tid =
      authUser?.id != null ? String(authUser.id).trim().toLowerCase() : "";
    if (
      tid &&
      inheritedTrainedTraineeIdsForCurrentRevision.has(tid) &&
      base !== null &&
      base !== "Partially Trained" &&
      !/\bStep\b/i.test(String(base || "")) &&
      base === "Not Trained"
    ) {
      return "Trained";
    }
    return base;
  }, [
    viewerAggregateTrainingStatusLabel,
    inheritedTrainedTraineeIdsForCurrentRevision,
    authUser?.id,
  ]);

  /** Give Training → Select Trainee(s): hide users with completed training or sub-aprr trainer flag. */
  const traineeOptionsForGiveTraining = useMemo(() => {
    const list = Array.isArray(giveTrainingTraineeUsers)
      ? giveTrainingTraineeUsers
      : [];
    const seen = new Set();
    const options = [];
    for (const u of list) {
      if (!u) continue;
      if (userIsTrainingDoneOrTrainerForSubAprRel(u)) continue;
      const id = u.id ?? u.user_id ?? u.user_ref;
      if (id == null) continue;
      const sid = String(id);
      if (seen.has(sid)) continue;
      seen.add(sid);
      const name =
        `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || sid;
      options.push({ id: sid, label: name });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [giveTrainingTraineeUsers]);

  /** Get Training → Select the Trainer: show if completed training or sub-aprr trainer flag. */
  const selectTrainerModalOptions = useMemo(() => {
    const list = Array.isArray(selectTrainerUsers) ? selectTrainerUsers : [];
    const out = [];
    const seen = new Set();
    for (const u of list) {
      if (!u) continue;
      if (!userIsTrainingDoneOrTrainerForSubAprRel(u)) continue;
      const id = u.id ?? u.user_id ?? u.user_ref;
      if (id == null) continue;
      const sid = String(id);
      if (seen.has(sid)) continue;
      seen.add(sid);
      const label =
        `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
        u.email ||
        sid;
      out.push({ id: sid, label });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [selectTrainerUsers]);

  const selectTrainerModalColumns = useMemo(
    () => chunkUserOptionsIntoColumns(selectTrainerModalOptions),
    [selectTrainerModalOptions],
  );
  const selectTrainerColumnCount = Math.max(selectTrainerModalColumns.length, 1);

  /**
   * 1) GET training-log/get-all (procedure_revision_ref, trainee, part_ref, limit, offset).
   * 2) Build sub_aprr_rel_users: unique trainer ids from rows where part_number === null.
   * 3) GET user/get-all?part_ref=<part_id>&sub_aprr_rel_users=<json array> (no filterKey/filterValue).
   */
  useEffect(() => {
    if (!showSelectTrainerPopup || giveTrainingFlow) return;
    if (!revisionId || !authUser?.id) return;
    let cancelled = false;
    (async () => {
      setSelectTrainerModalBusy(true);
      dispatch(clearSelectTrainerUsers());
      try {
        if (!partRefForSelectTrainer) {
          return;
        }
        const logsPayload = await dispatch(
          getTrainingLogsForProcedureRevision({
            procedureRevisionId: String(revisionId).trim(),
            trainee: String(authUser.id).trim(),
            limit: 100,
            offset: 0,
            part_ref: partRefForSelectTrainer,
          }),
        ).unwrap();
        if (cancelled) return;
        const results =
          (logsPayload && Array.isArray(logsPayload.results) && logsPayload.results) ||
          (logsPayload && Array.isArray(logsPayload.data) && logsPayload.data) ||
          [];
        const subAprRelUserIds =
          buildSubAprRelUserIdsFromTrainingLogResults(results);
        await dispatch(
          fetchSelectTrainerUsers({
            partRef: partRefForSelectTrainer,
            subAprRelUserIds,
          }),
        ).unwrap();
      } catch {
        if (!cancelled) dispatch(clearSelectTrainerUsers());
      } finally {
        if (!cancelled) setSelectTrainerModalBusy(false);
      }
    })();
    return () => {
      cancelled = true;
      setSelectTrainerModalBusy(false);
    };
  }, [
    showSelectTrainerPopup,
    giveTrainingFlow,
    revisionId,
    partRefForSelectTrainer,
    authUser?.id,
    dispatch,
  ]);

  useEffect(() => {
    if (!showSelectTrainerPopup) {
      dispatch(clearSelectTrainerUsers());
      setSelectTrainerModalBusy(false);
    }
  }, [showSelectTrainerPopup, dispatch]);

  useEffect(() => {
    if (selectTrainerModalOptions.length === 0) {
      setSelectedTrainer("");
      return;
    }
    setSelectedTrainer((prev) =>
      selectTrainerModalOptions.some((o) => o.id === prev)
        ? prev
        : selectTrainerModalOptions[0].id,
    );
  }, [selectTrainerModalOptions]);

  const selectedTrainerLabel = useMemo(
    () =>
      selectTrainerModalOptions.find((o) => o.id === selectedTrainer)?.label ?? "",
    [selectTrainerModalOptions, selectedTrainer],
  );

  /** Give Training: logged-in user is the trainer; Get Training: session label from Select the Trainer (Next). */
  const trainingBarTrainerDisplayName = useMemo(() => {
    if (giveTrainingFlow) {
      return (
        `${authUser?.first_name ?? ""} ${authUser?.last_name ?? ""}`.trim() ||
        authUser?.email ||
        "—"
      );
    }
    const session = String(getTrainingSessionTrainerLabel || "").trim();
    if (session) return session;
    return selectedTrainerLabel || "—";
  }, [
    giveTrainingFlow,
    authUser,
    selectedTrainerLabel,
    getTrainingSessionTrainerLabel,
  ]);

  const giveTrainingSessionTraineeDisplayName = useMemo(
    () =>
      giveTrainingSessionTraineeEntries
        .map((e) => e.label)
        .filter(Boolean)
        .join(", "),
    [giveTrainingSessionTraineeEntries],
  );

  const traineeDisplayName = useMemo(() => {
    if (giveTrainingFlow) {
      const persisted = String(giveTrainingSessionTraineeDisplayName || "").trim();
      if (persisted) return persisted;
      if (giveTrainingSelectedTraineeIds.length > 0) {
        const labels = giveTrainingSelectedTraineeIds
          .map(
            (id) =>
              traineeOptionsForGiveTraining.find((o) => o.id === id)?.label,
          )
          .filter(Boolean);
        if (labels.length === 1) return labels[0];
        if (labels.length > 1) return labels.join(", ");
      }
      return "Trainee";
    }
    return (
      `${authUser?.first_name ?? ""} ${authUser?.last_name ?? ""}`.trim() ||
      authUser?.email ||
      "Trainee"
    );
  }, [
    giveTrainingFlow,
    giveTrainingSelectedTraineeIds,
    traineeOptionsForGiveTraining,
    giveTrainingSessionTraineeDisplayName,
    authUser,
  ]);

  const isLock = isLocked(currentStatus);

  /** Get Training → Select trainer → Next: user must tick steps on a released/view revision — do not block on isLock or Edit. */
  const trainingCheckmarkActive =
    isViewMode && !fromAuditTrail && trainingCheckmarkMode;

  // Determine if checkboxes should be enabled
  //
  // Tab Lock Behavior:
  // - Only the FIRST tab (by Web Locks API leader election) can edit
  // - Other tabs (Tab 2, Tab 3, etc.) remain LOCKED while Tab 1 is open
  // - When Tab 1 closes, Web Locks API automatically promotes Tab 2 to leader
  // - Tab 2 then becomes editable (isFirstTabByOrder changes to true)
  //
  // Header (procedure) checkbox is disabled if:
  // 1. Not the first tab by order (other tabs are always locked) - UNLESS bypassing tab order lock
  // 2. API lock exists and user_id doesn't match logged in user_id
  // 3. User doesn't have Edit permission
  // 4. Revision has an active lock owned by someone else
  const isLockedByOtherUser =
    lockStatus && lockStatus.lock && lockStatus.lock.user_id !== authUser?.id;

  // Only the tab that holds the revision lock (first tab) can edit; lock is held by provider so it is not released on navigate
  const shouldDisableCheckboxes =
    (!bypassTabOrderLock && !isFirstTabByOrder) ||
    (isApiLocked && lockedBy) ||
    (!canEdit && !trainingCheckmarkActive) ||
    isLockedByOtherUser;
  // Training checkmark flow (Get/Give Training) is view-only and should not be blocked by edit locks/tab-order.
  // Keep normal/edit flows unchanged by gating this override to trainingCheckmarkActive only.
  const isCheckboxEnabled = trainingCheckmarkActive ? true : !shouldDisableCheckboxes;

  // Node checkboxes (revision tree) should remain usable even on released/submitted/approved snapshots.
  // Keep tab-order, API lock, permission, and "locked by other user" protections intact.
  // Training checkmark flow (Get/Give Training) is always enabled.
  const shouldDisableNodesCheckboxes = trainingCheckmarkActive
    ? false
    : (!bypassTabOrderLock && !isFirstTabByOrder) ||
      (isApiLocked && lockedBy) ||
      !canEdit ||
      isLockedByOtherUser;

  // App bar workflow actions (Archive, Reject, etc.): do not use `isLock` — released/archived
  // snapshots disable node checkboxes but users with Archive permission must still archive.
  const documentAppBarDisabled =
    (!bypassTabOrderLock && !isFirstTabByOrder) ||
    (isApiLocked && lockedBy) ||
    !canEdit ||
    isLockedByOtherUser;

  // Determine if Create New Revision button should be shown (hide for "Rejected" status)
  const showCreateRevisionButton = currentStatus?.toLowerCase() !== "rejected";

  // Create New Revision: disable only for permission or another user's API lock on this revision.
  // Do not tie to tab-order / isFirstTabByOrder — that blocked the button when the revision was not locked.
  const isCreateButtonDisabled = !canCreate || isLockedByOtherUser;

  // Revision row navigation: only block when another user holds the API lock.
  // Tab-order (Web Locks) applies to editing the same revision in multiple tabs — it should not
  // prevent navigating to another revision via the table (different route / lock scope).
  const isRevisionTableReadOnly = isLockedByOtherUser;

  // Recursively collect all version_id_ref from nodes structure (UUID strings only; skips bad refs)
  const collectVersionIds = (nodes) => {
    const raw = [];
    const traverseNode = (node) => {
      if (node.version_id_ref) raw.push(node.version_id_ref);
      if (node.steps && Array.isArray(node.steps)) {
        node.steps.forEach((step) => {
          if (step.version_id_ref) raw.push(step.version_id_ref);
          if (step.substeps && Array.isArray(step.substeps)) {
            step.substeps.forEach((substep) => {
              if (substep.version_id_ref) raw.push(substep.version_id_ref);
              if (substep.detail?.version_id_ref) {
                raw.push(substep.detail.version_id_ref);
              }
              if (substep.children && Array.isArray(substep.children)) {
                substep.children.forEach((child) => {
                  if (child.version_id_ref) raw.push(child.version_id_ref);
                  if (child.detail?.version_id_ref) {
                    raw.push(child.detail.version_id_ref);
                  }
                });
              }
            });
          }
          if (step.children && Array.isArray(step.children)) {
            step.children.forEach((child) => {
              if (child.version_id_ref) raw.push(child.version_id_ref);
              if (child.detail?.version_id_ref) {
                raw.push(child.detail.version_id_ref);
              }
            });
          }
        });
      }
    };
    if (nodes && Array.isArray(nodes)) {
      nodes.forEach((section) => traverseNode(section));
    }
    return uniqueVersionIdRefs(raw);
  };

  // State to accumulate all version histories from all nodes
  const [allVersionHistories, setAllVersionHistories] = useState([]);
  const [versionHistoryTableLoading, setVersionHistoryTableLoading] =
    useState(false);
  const [versionIdsFromLtreeApi, setVersionIdsFromLtreeApi] = useState([]);

  // Always call get-version-ids whenever ltree_path changes.
  useEffect(() => {
    const ltreePath = currentRevision?.tree_node_details?.ltree_path;
    dispatch(getVersionIdsByLtreePath({ ltree_path: ltreePath }))
      .unwrap()
      .then((ids) => {
        setVersionIdsFromLtreeApi(Array.isArray(ids) ? ids : []);
      })
      .catch((error) => {
        console.error("[Document][VersionIdsAPI] failed", error);
        setVersionIdsFromLtreeApi([]);
      });
  }, [
    dispatch,
    revisionId,
    currentRevision?.version_id_ref,
    currentRevision?.tree_node_details?.ltree_path,
  ]);

  // Fetch version history when popup opens
  useEffect(() => {
    if (!showVersionHistory) {
      setAllVersionHistories([]);
      setVersionHistoryTableLoading(false);
      return;
    }

    if (loading.revisionData) {
      setVersionHistoryTableLoading(true);
      return;
    }

    setVersionHistoryTableLoading(true);
    let cancelled = false;

    const fetchAllHistories = async () => {
      try {
        const fallbackVersionIds =
          allNodesData && Array.isArray(allNodesData)
            ? collectVersionIds(allNodesData)
            : [];
        const fromLtree = uniqueVersionIdRefs(versionIdsFromLtreeApi || []);
        let versionIds = uniqueVersionIdRefs([
          ...fromLtree,
          ...fallbackVersionIds,
        ]);
        const revRoot = normalizeVersionIdRef(
          currentRevision?.version_id_ref,
        );
        if (revRoot && !versionIds.includes(revRoot)) {
          versionIds.push(revRoot);
        }
        versionIds = uniqueVersionIdRefs(versionIds);

        if (versionIds.length === 0) {
          if (!cancelled) {
            setAllVersionHistories([]);
          }
          return;
        }

        const applyRows = (rows) => {
          const list = Array.isArray(rows) ? rows : [];
          const uniqueHistories = Array.from(
            new Map(
              list.map((item) => [versionHistoryRowDedupeKey(item), item]),
            ).values(),
          );
          if (!cancelled) {
            setAllVersionHistories(uniqueHistories);
          }
        };

        const matchesThisRevision = (row) => {
          const pr = row?.version_content?.procedure_revision_id;
          const prp = row?.parent_version_data?.version_content
            ?.procedure_revision_id;
          if (pr == null && prp == null) return true;
          const id = String(revisionId ?? "");
          return String(pr ?? "") === id || String(prp ?? "") === id;
        };

        // 1) Prefer a single API call scoped to this procedure revision + part (if supported by API)
        try {
          const bulk = await dispatch(
            getAllVersionHistories({
              procedureRevisionId: revisionId,
              partRef: partId,
              limit: 200000,
              offset: 0,
            }),
          ).unwrap();
          const rows = extractVersionHistoryResults(bulk);
          if (rows.length > 0) {
            const filtered = rows.filter(matchesThisRevision);
            applyRows(filtered.length ? filtered : rows);
            return;
          }
        } catch (e) {
          console.warn(
            "[Document][VersionHistory] bulk by procedure_revision failed, will try batched version_id or per-id",
            e,
          );
        }

        // 2) One request with comma‑separated version_id (many backends support this)
        if (versionIds.length > 1) {
          try {
            const batched = await dispatch(
              getAllVersionHistories({
                versionId: versionIds.join(","),
                limit: 200000,
                offset: 0,
              }),
            ).unwrap();
            const rows = extractVersionHistoryResults(batched);
            if (rows.length > 0) {
              applyRows(rows);
              return;
            }
          } catch (e) {
            console.warn(
              "[Document][VersionHistory] batched version_id failed, falling back to per-id",
              e,
            );
          }
        }

        // 3) Fallback: one request per version id (legacy)
        const results = await Promise.all(
          versionIds.map((versionId) =>
            dispatch(
              getAllVersionHistories({
                versionId,
                limit: 200000,
                offset: 0,
              }),
            ).unwrap(),
          ),
        );
        if (cancelled) return;

        const combinedHistories = results.flatMap(extractVersionHistoryResults);
        applyRows(combinedHistories);
      } catch (error) {
        console.error("[Document][VersionHistory] fetchAllHistories failed", error);
        if (!cancelled) {
          setAllVersionHistories([]);
        }
      } finally {
        if (!cancelled) {
          setVersionHistoryTableLoading(false);
        }
      }
    };

    fetchAllHistories();
    return () => {
      cancelled = true;
      setVersionHistoryTableLoading(false);
    };
  }, [
    showVersionHistory,
    allNodesData,
    versionIdsFromLtreeApi,
    currentRevision?.version_id_ref,
    currentRevision?.tree_node_details?.ltree_path,
    loading.revisionData,
    dispatch,
    revisionId,
    partId,
  ]);

  // Refresh nodes and BOM when component mounts or when partId/revisionId changes
  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset overflow styles that may have been set by popups
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    // Increment refresh key to force RenderRevisionNodes to re-fetch data
    setNodesRefreshKey((prev) => prev + 1);
    // Also refresh BOM table
    if (refreshBomTable) {
      refreshBomTable();
    }
  }, [partId, revisionId, refreshBomTable]);

  // Refresh data when page becomes visible (user returns from editing)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        const returningFromViewRevision =
          sessionStorage.getItem(`returningFromViewRevision_${revisionId}`) ===
          "true";

        if (
          returningFromViewRevision &&
          authUser &&
          revisionId &&
          isFirstTabByOrder
        ) {
          try {
            const status = await getRevisionLockStatus(revisionId);
            setLockStatus(status);
            sessionStorage.removeItem(
              `returningFromViewRevision_${revisionId}`,
            );
          } catch (error) {
            // Failed to refresh lock status
          }
        }

        // Increment refresh key to force data reload
        setNodesRefreshKey((prev) => prev + 1);
        if (refreshBomTable) {
          refreshBomTable();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshBomTable, revisionId, authUser, isFirstTabByOrder]);

  // Fetch lock status for the document
  useEffect(() => {
    const fetchLockStatus = async () => {
      try {
        if (revisionId) {
          const status = await getRevisionLockStatus(revisionId);
          setLockStatus(status);
        }
      } catch (error) {
        setLockStatus(null);
      }
    };

    const lockTaskOnce = async () => {
      try {
        if (revisionId && authUser) {
          await lockTask({
            task_id: `revision_${revisionId}`,
            user_id: authUser.id,
            user_name:
              `${authUser.first_name || ""} ${authUser.last_name || ""}`.trim() ||
              authUser.email ||
              "Unknown User",
            action: "edit",
          });
          const status = await getRevisionLockStatus(revisionId);
          setLockStatus(status);
        }
      } catch (error) {
        if (revisionId && error?.response?.status === 423) {
          try {
            const status = await getRevisionLockStatus(revisionId);
            setLockStatus(status);
          } catch (e) {
            // Failed to refresh lock status after 423
          }
        }
      }
    };

    const pingTaskInterval = async () => {
      try {
        if (!revisionId || !authUser) return;
        const status = await getRevisionLockStatus(revisionId);
        if (status?.lock?.user_id !== authUser.id) return;
        await pingTask({
          task_id: `revision_${revisionId}`,
          user_id: authUser.id,
        });
      } catch (error) {
        // Failed to send ping task
      }
    };

    // Initial fetch lock status and lock task once
    fetchLockStatus();
    lockTaskOnce();

    // Set up pingTask every minute (60000ms) instead of lock status refresh every 5 seconds
    const interval = setInterval(pingTaskInterval, 60000);

    const handlePageUnload = async () => {
      try {
        if (
          revisionId &&
          authUser &&
          lockStatus?.lock?.user_id === authUser.id
        ) {
          await unlockTask({
            task_id: `revision_${revisionId}`,
            user_id: authUser.id,
            user_name:
              `${authUser.first_name || ""} ${authUser.last_name || ""}`.trim() ||
              authUser.email ||
              "Unknown User",
            action: "edit",
          });
        }
      } catch (error) {
        // Failed to unlock task on page unload
      }
    };

    window.addEventListener("beforeunload", handlePageUnload);
    window.addEventListener("unload", handlePageUnload);

    // Cleanup interval and event listeners on unmount
    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", handlePageUnload);
      window.removeEventListener("unload", handlePageUnload);

      // Also unlock on component unmount
      handlePageUnload();
    };
  }, [revisionId, authUser, isFirstTabByOrder]);

  // Function to generate error message from lock status
  const generateLockErrorMessage = useCallback(
    (lockStatusData, revisionDataArray) => {
      if (isViewMode && !fromAuditTrail) {
        return null;
      }
      // Multi-tab same revision: non-leader tab must run before `isLock` — released/approved views
      // also use isLock=true for BOM semantics, and the old order returned null here before tab-order ran.
      if (
        !isFirstTabByOrder &&
        !bypassTabOrderLock &&
        !isRevisionLocked &&
        canEdit &&
        !(isApiLocked && lockedBy)
      ) {
        const currentUserName =
          `${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim() ||
          "You";
        return `Locked by ${currentUserName}`;
      }

      // Released/archived: show message only when another user holds the edit lock (same-user cases handled above).
      if (isLock) {
        const lock = lockStatusData?.lock;
        if (lock == null) return null;
        const lockUid = lock.user_id != null ? String(lock.user_id) : "";
        const authUid = authUser?.id != null ? String(authUser.id) : "";
        if (lockUid && authUid && lockUid !== authUid) {
          return `Locked by ${lock.user_name}`;
        }
        return null;
      }

      if (
        !lockStatusData ||
        lockStatusData.lock === null ||
        lockStatusData.lock === undefined
      ) {
        return null;
      }

      const lock = lockStatusData.lock;

      if (
        lock.user_id === authUser?.id &&
        (isFirstTabByOrder || bypassTabOrderLock)
      ) {
        return null;
      }

      if (
        lock.user_id === authUser?.id &&
        !isFirstTabByOrder &&
        !bypassTabOrderLock
      ) {
        const currentUserName =
          `${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim() ||
          "You";
        return `Locked by ${currentUserName}`;
      }

      if (lock.user_id !== authUser?.id) {
        return `Locked by ${lock.user_name}`;
      }

      return null;
    },
    [
      authUser,
      revisionId,
      isFirstTabByOrder,
      bypassTabOrderLock,
      isApiLocked,
      lockedBy,
      isLock,
      isRevisionLocked,
      canEdit,
      isViewMode,
      fromAuditTrail,
    ],
  );

  useEffect(() => {
    const newErrorMessage = generateLockErrorMessage(lockStatus, revisionData);
    setLockErrorMessage(newErrorMessage);
  }, [lockStatus, revisionData, generateLockErrorMessage]);

  // Check revision lock status to disable checkboxes if revision is locked
  useEffect(() => {
    const checkRevisionLockStatus = async () => {
      try {
        if (revisionId) {
          const revisionStatus = await getRevisionLockStatus(revisionId);
          setRevisionLockStatus(revisionStatus);

          const isLocked =
            authUser?.id != null &&
            revisionStatus?.lock &&
            revisionStatus.lock.user_id !== authUser.id;

          setIsRevisionLocked(!!isLocked);
        }
      } catch (error) {
        setRevisionLockStatus(null);
        setIsRevisionLocked(false);
      }
    };

    const pingRevisionTaskInterval = async () => {
      try {
        if (!revisionId || !authUser) return;
        const status = await getRevisionLockStatus(revisionId);
        if (status?.lock?.user_id !== authUser.id) return;
        await pingTask({
          task_id: `revision_${revisionId}`,
          user_id: authUser.id,
        });
      } catch (error) {
        // Failed to send revision ping task
      }
    };

    // Initial check
    checkRevisionLockStatus();

    // Set up pingTask every minute (60000ms) instead of revision lock status refresh every 30 seconds
    const interval = setInterval(pingRevisionTaskInterval, 60000);

    // Cleanup interval on unmount
    return () => {
      clearInterval(interval);
    };
  }, [revisionId, authUser?.id]);

  const handleDocumentRefresh = useCallback(() => {
    refreshRevisionData();
    if (refreshBomTable) {
      refreshBomTable();
    }
    setNodesRefreshKey((prev) => prev + 1);
  }, [refreshBomTable, refreshRevisionData]);

  const collapsedSectionHintStyle = {
    fontSize: "13px",
    color: "#6c757d",
    fontStyle: "italic",
    margin: 0,
    paddingBottom: "6px",
    lineHeight: 1.35,
    width: "100%",
  };

  const collapsibleSectionHeadingStyle = {
    marginTop: "1em",
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
  };

  const collapsibleSectionTitleRowStyle = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "10px",
    rowGap: "2px",
  };

  /** Keep section-to-next-heading spacing uniform. */
  const revisionsTableSectionBlockStyle = {
    marginTop: "30px",
    marginBottom: "30px",
  };
  const trainingLogSectionBlockStyle = {
    paddingTop: 0,
    marginBottom: "30px",
  };

  const renderSectionToggle = (isOpen, onToggle) => (
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: "30px",
        height: "21px",
        border: "1px solid #6C757D",
        borderRadius: "5px",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        opacity: 1,
        marginTop: 0,
        flexShrink: 0,
        paddingTop: "8px",
        paddingRight: "15px",
        paddingBottom: "8px",
        paddingLeft: "15px",
      }}
      aria-label={isOpen ? "Collapse section" : "Expand section"}
      title={isOpen ? "Collapse" : "Expand"}
    >
      <span
        style={{
          display: "inline-block",
          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease",
          color: "#6C757D",
          fontSize: "12px",
          lineHeight: 1,
        }}
      >
        ▼
      </span>
    </button>
  );

  // Handle document header checkbox click for lock functionality
  const handleDocumentHeaderCheckboxClick = useCallback(() => {
    sessionStorage.setItem(`fromDocument_${revisionId}`, "true");

    const tabId = sessionStorage.getItem("tab-id");
    if (bypassTabOrderLock || isFirstTabByOrder) {
      sessionStorage.setItem(`wasLeader_${revisionId}_${tabId}`, "true");
    }

    if (partId && revisionId) {
      navigate(`/view-revision/${partId}/${revisionId}`);
    }
  }, [
    authUser,
    partId,
    revisionId,
    navigate,
    bypassTabOrderLock,
    isFirstTabByOrder,
  ]);

  const handleTrainingProcedureHeaderClick = useCallback(() => {
    setTrainingCheckmarkSelection((prev) => {
      if (prev?.scope === "procedure") return null;
      return { scope: "procedure" };
    });
  }, []);

  /** Training checkmark: toggle multiple step selections — no view-step navigation. */
  const handleTrainingCheckmarkStepNavigate = useCallback(
    (nodeType, nodeData) => {
      if (nodeType !== "step") return;
      const id = nodeData.tree_node_id;
      setTrainingCheckmarkSelection((prev) => {
        if (prev?.scope === "procedure") {
          return { scope: "steps", treeNodeIds: [id] };
        }
        if (prev?.scope === "steps") {
          const ids = [...(prev.treeNodeIds || [])];
          const idx = ids.indexOf(id);
          if (idx >= 0) ids.splice(idx, 1);
          else ids.push(id);
          return ids.length === 0 ? null : { scope: "steps", treeNodeIds: ids };
        }
        if (prev?.scope === "step") {
          if (prev.treeNodeId === id) return null;
          return { scope: "steps", treeNodeIds: [prev.treeNodeId, id] };
        }
        return { scope: "steps", treeNodeIds: [id] };
      });
    },
    [],
  );

  const handleToggleGiveTrainee = useCallback((id) => {
    setGiveTrainingSelectedTraineeIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  }, []);

  const handleSelectTraineeNext = useCallback(() => {
    if (giveTrainingSelectedTraineeIds.length === 0) {
      dispatch(
        showSnackbar({
          type: "error",
          message: "Please select at least one trainee.",
        }),
      );
      return;
    }
    const entries = giveTrainingSelectedTraineeIds.map((id) => {
      const sid = String(id);
      const label =
        traineeOptionsForGiveTraining.find((o) => o.id === sid)?.label ?? sid;
      return { id: sid, label };
    });
    setGiveTrainingSessionTraineeEntries(entries);
    setShowSelectTraineePopup(false);
    dispatch(clearGiveTrainingTraineeUsers());
    setGiveTrainingFlow(true);
    setTrainingCheckmarkMode(true);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }, [
    giveTrainingSelectedTraineeIds,
    traineeOptionsForGiveTraining,
    dispatch,
  ]);

  /**
   * Give Training → Select Trainee(s): training-log/get-all then
   * user/get-all?part_ref=<part_id>&sub_aprr_rel_users (no filterKey/filterValue).
   */
  useEffect(() => {
    if (!showSelectTraineePopup || !isViewMode || !partId || !revisionId) return;
    if (!authUser?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const logsPayload = await dispatch(
          getTrainingLogsForProcedureRevision({
            procedureRevisionId: String(revisionId).trim(),
            trainee: String(authUser.id).trim(),
            limit: 100,
            offset: 0,
            part_ref: String(partId).trim(),
          }),
        ).unwrap();
        if (cancelled) return;
        const results =
          (logsPayload && Array.isArray(logsPayload.results) && logsPayload.results) ||
          (logsPayload && Array.isArray(logsPayload.data) && logsPayload.data) ||
          [];
        const fromTrainingLogs =
          buildSubAprRelUserIdsFromTrainingLogResults(results);
        const fromSameLetterRevisions =
          collectWorkflowActorUserIdsFromSameLetterRevisions(
            revisionData,
            currentRevision?.rev,
          );
        const subAprRelUserIds = mergeGiveTrainingSubAprRelUserIds(
          fromTrainingLogs,
          fromSameLetterRevisions,
        );
        await dispatch(
          fetchGiveTrainingTraineeUsers({
            limit: 100,
            offset: 0,
            partRef: String(partId).trim(),
            subAprRelUserIds,
          }),
        ).unwrap();
      } catch (err) {
        if (cancelled) return;
        dispatch(clearGiveTrainingTraineeUsers());
        const msg =
          (typeof err === "string" && err) ||
          err?.message ||
          "Could not load trainee list.";
        dispatch(
          showSnackbar({
            type: "error",
            message: typeof msg === "string" ? msg : JSON.stringify(msg),
          }),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showSelectTraineePopup,
    isViewMode,
    partId,
    revisionId,
    revisionData,
    currentRevision?.rev,
    authUser?.id,
    dispatch,
  ]);

  const trainingRevisionNodesCheckboxState = useMemo(() => {
    const selectedIds = new Set();

    // Pre-check already trained steps (green) in view-only checkmark flow.
    if (trainedStepTreeNodeIdsSet !== null) {
      for (const id of trainedStepTreeNodeIdsSet) selectedIds.add(id);
    }

    // Merge with user-selected steps (blue) without breaking existing selection behavior.
    if (trainingCheckmarkSelection?.scope === "steps") {
      for (const id of trainingCheckmarkSelection.treeNodeIds || []) {
        selectedIds.add(id);
      }
    } else if (trainingCheckmarkSelection?.scope === "step") {
      if (trainingCheckmarkSelection.treeNodeId) {
        selectedIds.add(trainingCheckmarkSelection.treeNodeId);
      }
    }

    if (selectedIds.size === 0) return undefined;

    return {
      currentNodeType: "step",
      selectedStepIds: Array.from(selectedIds),
      /** Give Training checkmark screen: use mint-style selected checkbox (not default blue). */
      giveTrainingCheckmark: giveTrainingFlow === true,
    };
  }, [trainingCheckmarkSelection, trainedStepTreeNodeIdsSet, giveTrainingFlow]);

  useEffect(() => {
    if (!isViewMode || fromAuditTrail) {
      setTrainingCheckmarkSelection(null);
      setShowCompleteTrainingModal(false);
      setTrainedStepNumbersSet(null);
      setPartiallyTrainedStepNumbersSet(null);
      setAllStepsTrained(false);
      setTrainedStepTreeNodeIdsSet(null);
      setCompleteTrainingTraineeIds([]);
      setCompleteTrainingTraineeIndex(0);
      setGiveTrainingFlow(false);
      setGiveTrainingSelectedTraineeIds([]);
      return;
    }
    if (!trainingCheckmarkMode) {
      setTrainingCheckmarkSelection(null);
      setShowCompleteTrainingModal(false);
      setTrainedStepTreeNodeIdsSet(null);
      setCompleteTrainingTraineeIds([]);
      setCompleteTrainingTraineeIndex(0);
      setGiveTrainingFlow(false);
      setGiveTrainingSelectedTraineeIds([]);
    }
  }, [isViewMode, fromAuditTrail, trainingCheckmarkMode]);

  useEffect(() => {
    if (!isViewMode || fromAuditTrail || !revisionId) {
      setViewerAggregateTrainingStatusLabel(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { status } = await fetchTrainingStatusLabelForProcedureRevisionForTrainee(
        dispatch,
        revisionId,
        authUser?.id,
        partId,
      );
      if (!cancelled) setViewerAggregateTrainingStatusLabel(String(status ?? ""));
    })();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    isViewMode,
    fromAuditTrail,
    revisionId,
    trainingStatusRefreshKey,
    partId,
    authUser?.id,
  ]);

  /** Give Training: trainer is logged in — scope logs to the selected trainee(s), not `authUser`. */
  const traineeIdForPerStepTrainingLog = useMemo(() => {
    if (giveTrainingFlow && giveTrainingSelectedTraineeIds?.length > 0) {
      return giveTrainingSelectedTraineeIds[0];
    }
    return authUser?.id;
  }, [giveTrainingFlow, giveTrainingSelectedTraineeIds, authUser?.id]);

  // Load per-step training from latest log `trained_to` (matches GET /training-log/get-all).
  // Used on the main view (Get Training screen) and in the checkmark flow.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!isViewMode || fromAuditTrail || !revisionId) {
        return;
      }

      if (!Array.isArray(allNodesData)) {
        return;
      }

      try {
        const { log, status } = await fetchLatestTrainingLogForProcedureRevisionForTrainee(
          dispatch,
          revisionId,
          traineeIdForPerStepTrainingLog,
          partId,
        );
        if (cancelled) return;

        const derived = derivePerStepTrainingSetsFromLog(log, allNodesData);

        if (derived.allStepsTrained) {
          setAllStepsTrained(true);
          setTrainedStepNumbersSet(new Set());
          setPartiallyTrainedStepNumbersSet(new Set());
          return;
        }

        setAllStepsTrained(false);

        if (derived.fullyTrained.size > 0 || derived.partial.size > 0) {
          setTrainedStepNumbersSet(derived.fullyTrained);
          setPartiallyTrainedStepNumbersSet(derived.partial);
          return;
        }

        if (status === "Trained") {
          setAllStepsTrained(true);
          setTrainedStepNumbersSet(new Set());
          setPartiallyTrainedStepNumbersSet(new Set());
          return;
        }

        if (typeof status === "string" && /\bStep\b/i.test(status)) {
          const nums = (status.match(/\d+/g) || [])
            .map((n) => parseInt(n, 10))
            .filter((n) => Number.isFinite(n) && n > 0);
          setTrainedStepNumbersSet(new Set(nums));
          setPartiallyTrainedStepNumbersSet(new Set());
          return;
        }

        const inheritedTid =
          authUser?.id != null
            ? String(authUser.id).trim().toLowerCase()
            : "";
        if (
          inheritedTid &&
          inheritedTrainedTraineeIdsForCurrentRevision.has(inheritedTid)
        ) {
          setAllStepsTrained(true);
          setTrainedStepNumbersSet(new Set());
          setPartiallyTrainedStepNumbersSet(new Set());
          return;
        }

        setTrainedStepNumbersSet(new Set());
        setPartiallyTrainedStepNumbersSet(new Set());
      } catch {
        if (cancelled) return;
        setAllStepsTrained(false);
        setTrainedStepNumbersSet(new Set());
        setPartiallyTrainedStepNumbersSet(new Set());
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    isViewMode,
    fromAuditTrail,
    revisionId,
    allNodesData,
    trainingStatusRefreshKey,
    partId,
    authUser?.id,
    inheritedTrainedTraineeIdsForCurrentRevision,
    traineeIdForPerStepTrainingLog,
  ]);

  // Derive which step tree_node_ids correspond to the trained global step numbers.
  // This lets us pre-check those step checkboxes in green (view-only checkmark flow).
  useEffect(() => {
    if (!isViewMode || fromAuditTrail || !trainingCheckmarkMode) {
      setTrainedStepTreeNodeIdsSet(null);
      return;
    }
    if (!Array.isArray(allNodesData)) {
      setTrainedStepTreeNodeIdsSet(null);
      return;
    }

    // If training data is still loading, don't pre-check anything.
    if (
      !allStepsTrained &&
      (trainedStepNumbersSet === null || partiallyTrainedStepNumbersSet === null)
    ) {
      setTrainedStepTreeNodeIdsSet(null);
      return;
    }

    const compareSiblingOrders = (a, b) => {
      const intA = parseInt(a, 10);
      const intB = parseInt(b, 10);
      if (Number.isNaN(intA) && Number.isNaN(intB)) return 0;
      if (Number.isNaN(intA)) return 1;
      if (Number.isNaN(intB)) return -1;
      return intA - intB;
    };

    const sortedSections = [...allNodesData].sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // latest comes first
    });

    let globalStepNumber = 0;
    const ids = new Set();

    for (const section of sortedSections) {
      const steps = Array.isArray(section.steps) ? section.steps : [];
      for (const step of steps) {
        globalStepNumber += 1;
        if (step?.is_deleted) continue;

        const shouldMarkTrained =
          allStepsTrained ||
          (trainedStepNumbersSet?.has &&
            trainedStepNumbersSet.has(globalStepNumber)) ||
          (partiallyTrainedStepNumbersSet?.has &&
            partiallyTrainedStepNumbersSet.has(globalStepNumber));

        if (shouldMarkTrained && step?.tree_node_id) {
          ids.add(step.tree_node_id);
        }
      }
    }

    setTrainedStepTreeNodeIdsSet(ids);
  }, [
    isViewMode,
    fromAuditTrail,
    trainingCheckmarkMode,
    allNodesData,
    allStepsTrained,
    trainedStepNumbersSet,
    partiallyTrainedStepNumbersSet,
  ]);

  const handleTrainingCheckmarkNextClick = useCallback(() => {
    if (!trainingCheckmarkSelection) {
      dispatch(
        showSnackbar({
          type: "error",
          message:
            "Please select the procedure or one or more steps before continuing.",
        }),
      );
      return;
    }
    if (
      trainingCheckmarkSelection.scope === "steps" &&
      !(trainingCheckmarkSelection.treeNodeIds?.length > 0)
    ) {
      dispatch(
        showSnackbar({
          type: "error",
          message:
            "Please select the procedure or one or more steps before continuing.",
        }),
      );
      return;
    }
    const selectedStepIds =
      trainingCheckmarkSelection?.scope === "steps"
        ? trainingCheckmarkSelection.treeNodeIds || []
        : trainingCheckmarkSelection?.scope === "step" &&
            trainingCheckmarkSelection.treeNodeId
          ? [trainingCheckmarkSelection.treeNodeId]
          : [];
    const hasNonTrainedStepSelected =
      selectedStepIds.length > 0
        ? selectedStepIds.some(
            (id) =>
              !(
                trainedStepTreeNodeIdsSet &&
                trainedStepTreeNodeIdsSet instanceof Set &&
                trainedStepTreeNodeIdsSet.has(id)
              ),
          )
        : false;
    if (
      (trainingCheckmarkSelection.scope === "steps" ||
        trainingCheckmarkSelection.scope === "step") &&
      !hasNonTrainedStepSelected
    ) {
      dispatch(
        showSnackbar({
          type: "error",
          message:
            "Please select the procedure or one or more steps before continuing.",
        }),
      );
      return;
    }
    const queue = giveTrainingFlow
      ? giveTrainingSelectedTraineeIds
      : authUser?.id != null
        ? [String(authUser.id)]
        : [];

    if (!Array.isArray(queue) || queue.length === 0) {
      dispatch(
        showSnackbar({
          type: "error",
          message: "No trainee selected for this training session.",
        }),
      );
      return;
    }

    setCompleteTrainingTraineeIds(queue);
    setCompleteTrainingTraineeIndex(0);
    setShowCompleteTrainingModal(true);
  }, [
    trainingCheckmarkSelection,
    dispatch,
    giveTrainingFlow,
    giveTrainingSelectedTraineeIds,
    authUser?.id,
    trainedStepTreeNodeIdsSet,
  ]);

  const handleCompleteTrainingConfirm = useCallback(
    async (result) => {
      if (result?.error) {
        dispatch(
          showSnackbar({
            type: "error",
            message: result.error,
          }),
        );
        return { error: result.error };
      }

      if (!revisionId) {
        const msg = "Missing procedure revision.";
        dispatch(showSnackbar({ type: "error", message: msg }));
        return { error: msg };
      }
      if (!authUser?.id) {
        const msg = "User session is not available.";
        dispatch(showSnackbar({ type: "error", message: msg }));
        return { error: msg };
      }
      if (
        !giveTrainingFlow &&
        !String(getTrainingSessionTrainerId || selectedTrainer || "").trim()
      ) {
        const msg = "Trainer is required.";
        dispatch(showSnackbar({ type: "error", message: msg }));
        return { error: msg };
      }
      if (giveTrainingFlow && !giveTrainingSelectedTraineeIds?.length) {
        const msg = "No trainee selected for this training session.";
        dispatch(showSnackbar({ type: "error", message: msg }));
        return { error: msg };
      }
      if (!allNodesData || !Array.isArray(allNodesData)) {
        const msg =
          "Procedure steps are still loading. Please wait and try again.";
        dispatch(showSnackbar({ type: "warning", message: msg }));
        return { error: msg };
      }

      // IMPORTANT:
      // In "Checkmark Where Trained" view flow, we pre-check already-trained steps
      // for UI, but `trainingCheckmarkSelection` only contains user-selected steps.
      // For the update payload, we need to union both so already-trained steps
      // (e.g. Step 2) are included in `trained_to`.
      const effectiveTrainingCheckmarkSelection = (() => {
        // Give Training: one POST per selected trainee from the same checkmarks; do not union
        // `trainedStepTreeNodeIdsSet` — after trainee 1 saves, that set reflects trainee 1 and would
        // bleed into trainee 2's `trained_to` and empty `tree_node_id` for step-only selection.
        if (giveTrainingFlow) {
          return trainingCheckmarkSelection;
        }
        if (
          !trainingCheckmarkSelection ||
          !trainedStepTreeNodeIdsSet ||
          !(trainedStepTreeNodeIdsSet instanceof Set) ||
          trainedStepTreeNodeIdsSet.size === 0
        ) {
          return trainingCheckmarkSelection;
        }

        const trainedIds = trainedStepTreeNodeIdsSet;

        if (trainingCheckmarkSelection.scope === "steps") {
          const union = new Set([
            ...(trainingCheckmarkSelection.treeNodeIds || []),
            ...trainedIds,
          ]);
          return { scope: "steps", treeNodeIds: Array.from(union) };
        }

        if (trainingCheckmarkSelection.scope === "step") {
          const union = new Set([
            trainingCheckmarkSelection.treeNodeId,
            ...trainedIds,
          ]);
          const ids = Array.from(union).filter(Boolean);
          if (ids.length <= 1) {
            return { scope: "step", treeNodeId: ids[0] || null };
          }
          return { scope: "steps", treeNodeIds: ids };
        }

        // Procedure scope ("select all") should remain as-is.
        return trainingCheckmarkSelection;
      })();

      const trainedToInner = buildTrainedToPayload(
        allNodesData,
        effectiveTrainingCheckmarkSelection,
      );
      if (
        (effectiveTrainingCheckmarkSelection?.scope === "steps" ||
          effectiveTrainingCheckmarkSelection?.scope === "step") &&
        trainedToInner.steps.length === 0
      ) {
        const msg = "Could not load the selected step(s) for the training log.";
        dispatch(showSnackbar({ type: "error", message: msg }));
        return { error: msg };
      }

      const procedureTreeNodeId =
        currentRevision?.tree_node_details?.tree_node_id;
      if (!procedureTreeNodeId || String(procedureTreeNodeId).trim() === "") {
        const msg =
          "Procedure tree is not loaded for this revision. Cannot save training log.";
        dispatch(showSnackbar({ type: "error", message: msg }));
        return { error: msg };
      }

      // Outer `tree_node_id` must reflect **this** selection only. `effectiveTrainingCheckmarkSelection`
      // unions already-trained steps for `data.steps`; including those here wrongly picks Step 1 in
      // document order when the user only selected Step 2.
      const trainedToOuterTreeNodeId = resolveTrainedToOuterTreeNodeId({
        procedureTreeNodeId,
        trainingCheckmarkSelection,
        allNodesData,
      });

      try {
      const dateOfTraining = new Intl.DateTimeFormat("en-CA", {
        timeZone: getAppTimezone(appSetup),
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());

      const trainerIdForPayload = giveTrainingFlow
        ? String(authUser.id)
        : String(
            getTrainingSessionTrainerId || selectedTrainer,
          ).trim();
      const traineeIdForPayload = giveTrainingFlow
        ? String(
            completeTrainingTraineeIds?.[completeTrainingTraineeIndex] ??
              giveTrainingSelectedTraineeIds?.[0],
          )
        : String(authUser.id);

      const audit_trail_data = await getAuditTrailData({
        action: "CREATE_TRAINING_LOG",
        comment: "Complete training from document revision view",
        version_content: {
          procedure_revision_ref: revisionId,
          part_ref: partId,
          training_flow: giveTrainingFlow ? "give_training" : "get_training",
        },
      });

      const sigMethods = localStorage.signature_auth_methods || "";
      const passwordIncluded = sigMethods.includes("password");

      const trainingLogIsComplete =
        trainedToInner?.selection_scope === "procedure" &&
        trainedToInner?.is_complete === true;

      const trainedSetForCreateLogTreeNodeIds =
        giveTrainingFlow && trainingCheckmarkSelection?.scope === "steps"
          ? new Set()
          : trainedStepTreeNodeIdsSet;

      const createTrainingTreeNodeIds = buildCreateTrainingLogTreeNodeIdArray({
        procedureTreeNodeId,
        trainingCheckmarkSelection,
        allNodesData,
        trainedStepTreeNodeIdsSet: trainedSetForCreateLogTreeNodeIds,
      });

      const payload = {
        date_of_training: dateOfTraining,
        trainer: trainerIdForPayload,
        trainer_signature: result.trainerSignature,
        tree_node_id: createTrainingTreeNodeIds,
        is_complete: trainingLogIsComplete,
        trainee: traineeIdForPayload,
        trainee_signature: result.traineeSignature,
        procedure_revision_ref: revisionId,
        /** Procedure/revision tree root for this route (always); distinct from `trained_to.tree_node_id` when training a single step. */
        revision_tree_node_id: String(procedureTreeNodeId).trim(),
        // Get Training + Give Training: identical trained_to shape for POST /training-log/create.
        // Outer `tree_node_id` is the procedure root for full-procedure training; for step(s) it is
        // the selected step's tree_node_id (first in document order when multiple).
        trained_to: {
          tree_node_id: trainedToOuterTreeNodeId,
          data: trainedToInner,
        },
        is_maintained_outside_system: false,
        audit_trail_data: {
          ...audit_trail_data,
          // Trainer's password from CompleteTrainingModal
          password: result.trainerPassword,
          hash: result.trainerPassword,
          // Training log confirmation should not require password at audit trail layer.
          is_password_required: false,
          is_trainner_password: true,
          trainner_password: result.trainerPassword,
          trainner_user_ref: trainerIdForPayload,
        },
      };

        const { status: currentTrainingStatus } =
          await fetchTrainingStatusLabelForProcedureRevisionForTrainee(
            dispatch,
            revisionId,
            traineeIdForPayload,
            partId,
          );
        const displayTrainingStatus =
          typeof currentTrainingStatus === "string" &&
          /\bStep\b/i.test(currentTrainingStatus)
            ? "Partially Trained"
            : currentTrainingStatus;

        // For this multi-step "Complete Training" flow, we create a new training log
        // for each trainee (even when status is "Partially Trained").
        const shouldUpdateExistingLog = false;

        if (shouldUpdateExistingLog) {
          const logsPayload = await dispatch(
            getTrainingLogsForProcedureRevision({
              procedureRevisionId: String(revisionId).trim().toLowerCase(),
              limit: 100,
              offset: 0,
              ...(partId ? { part_ref: String(partId).trim() } : {}),
            }),
          ).unwrap();

          const logs =
            (logsPayload &&
              Array.isArray(logsPayload.results) &&
              logsPayload.results) ||
            (logsPayload &&
              Array.isArray(logsPayload.data) &&
              logsPayload.data) ||
            (Array.isArray(logsPayload) ? logsPayload : []);

          const sortedLogs = [...logs].sort((a, b) => {
            const aDate =
              a?.date_of_training || a?.created_at || a?.updated_at || "";
            const bDate =
              b?.date_of_training || b?.created_at || b?.updated_at || "";
            return new Date(bDate).getTime() - new Date(aDate).getTime();
          });

          const latestTrainingLog = sortedLogs[0];
          const trainingLogId =
            latestTrainingLog?.training_log_id || latestTrainingLog?.id || null;

          if (!trainingLogId) {
            throw new Error(
              "Could not find existing training log to update for partially trained status.",
            );
          }

          const updateAuditTrailData = await getAuditTrailData({
            action: "UPDATE_TRAINING_LOG",
            comment: "Update training log from document revision view",
            version_content: {
              procedure_revision_ref: revisionId,
              part_ref: partId,
              training_flow: giveTrainingFlow
                ? "give_training"
                : "get_training",
            },
          });

          await dispatch(
            updateTrainingLog({
              id: trainingLogId,
              data: {
                ...payload,
                audit_trail_data: {
                  ...updateAuditTrailData,
                  is_trainner_password: true,
                  trainner_password: result.trainerPassword,
                  trainner_user_ref: trainerIdForPayload,
                },
              },
            }),
          ).unwrap();
        } else {
          // Pass payload as-is so audit_trail_data (including trainer password)
          // is respected by trainingLogSlice/createTrainingLog.
          await dispatch(createTrainingLog(payload)).unwrap();
        }
        dispatch(
          showSnackbar({
            type: "success",
            message: "Training completion recorded.",
          }),
        );
        refreshRevisionData();
        setTrainingStatusRefreshKey((k) => k + 1);
        const isLastTraineeInModal =
          completeTrainingTraineeIndex >=
          (completeTrainingTraineeIds?.length || 0) - 1;

        if (!isLastTraineeInModal) {
          setCompleteTrainingTraineeIndex((i) => i + 1);
          return {};
        }

        setTrainingCheckmarkMode(false);
        setShowSelectTrainerPopup(false);
        setGetTrainingSessionTrainerId("");
        setGetTrainingSessionTrainerLabel("");
        setGiveTrainingSessionTraineeEntries([]);
        if (!isReleasedRevision) {
          navigate("/404");
          return {};
        }
        if (revisionId) {
          sessionStorage.setItem(
            `document_is_view_mode_${revisionId}`,
            "true",
          );
        }
        setGlobalAppViewMode(true);
        navigate(
          {
            pathname: ".",
            search: location.search,
            hash: location.hash,
          },
          {
            replace: true,
            state: {
              ...(location.state || {}),
              isViewMode: true,
            },
          },
        );
        return {};
      } catch (err) {
        const rejected = err?.payload != null ? err.payload : err;
        const msg =
          (typeof rejected === "string" && rejected) ||
          rejected?.message ||
          rejected?.detail ||
          (typeof err === "string" && err) ||
          err?.message ||
          err?.detail ||
          err?.error ||
          "Failed to save training log.";
        dispatch(
          showSnackbar({
            type: "error",
            message: typeof msg === "string" ? msg : JSON.stringify(msg),
          }),
        );
        return { error: msg };
      }
    },
    [
      dispatch,
      revisionId,
      selectedTrainer,
      getTrainingSessionTrainerId,
      authUser?.id,
      allNodesData,
      trainingCheckmarkSelection,
      trainedStepTreeNodeIdsSet,
      appSetup,
      refreshRevisionData,
      navigate,
      location.search,
      location.hash,
      location.state,
      isReleasedRevision,
      giveTrainingFlow,
      giveTrainingSelectedTraineeIds,
      completeTrainingTraineeIds,
      completeTrainingTraineeIndex,
      partId,
      currentRevision,
    ],
  );

  // BOM: API when unlocked; else snapshot/node aggregate. Live steps often use step_data.parts_required
  // (past_step_data_json.bom_rows is used for released snapshots).
  const bomTableData = useMemo(() => {
    const mergeBomRow = (bomAggregator, row) => {
      const partNumber =
        row.partNumber || row.part_number || row.part_info?.part_number;
      if (!partNumber) return;

      const qty = parseFloat(row.qty ?? 0);
      const typeName =
        row.type || row.type_name || row.part_info?.type_info?.name || "";
      const description =
        row.description ||
        row.part_description ||
        row.part_info?.description ||
        "";
      const unitName =
        row.unit_name || row.part_info?.unit_info?.unit_name || "";
      const orders =
        row.type_orders ??
        row.part_info?.type_info?.orders ??
        row.orders ??
        row.part_info?.part_info?.type_info?.orders;

      if (!bomAggregator[partNumber]) {
        bomAggregator[partNumber] = {
          type_name: typeName,
          part_number: partNumber,
          part_description: description,
          qty,
          unit_name: unitName,
          orders,
        };
      } else {
        bomAggregator[partNumber].qty += qty;
      }
    };

    const aggregateFromNodes = (nodes) => {
      if (!Array.isArray(nodes)) return [];

      const bomAggregator = {};

      const traverse = (nodeList) => {
        if (!Array.isArray(nodeList)) return;
        nodeList.forEach((node) => {
          const pastData = node.detail?.past_step_data_json;
          if (pastData?.bom_rows && Array.isArray(pastData.bom_rows)) {
            pastData.bom_rows.forEach((row) => mergeBomRow(bomAggregator, row));
          }
          const partsReq = node.detail?.step_data?.parts_required;
          if (partsReq && Array.isArray(partsReq)) {
            partsReq.forEach((row) => mergeBomRow(bomAggregator, row));
          }
          if (node.steps) traverse(node.steps);
          if (node.substeps) traverse(node.substeps);
        });
      };

      traverse(nodes);
      return Object.values(bomAggregator);
    };

    if(fromAuditTrail){
      return aggregateFromNodes(cancel_screen_data?.data);
    }
    if (!isLock) {
      const apiRows = Array.isArray(bomTable) ? bomTable : [];
      if (apiRows.length > 0) return apiRows;
      if (!allNodesData) return [];
      return aggregateFromNodes(allNodesData);
    }

    if (!allNodesData) return [];
    return aggregateFromNodes(allNodesData);
  }, [isLock, bomTable, allNodesData,fromAuditTrail]);

  /** First `parts_required_id` from API BOM rows or step snapshots — used by Adjust Qty API. */
  const partsRequiredIdForAdjustQty = useMemo(() => {
    const idFromRow = (row) => {
      if (!row || typeof row !== "object") return null;
      const id =
        row.parts_required_id ??
        row.parts_required_id_ref ??
        row.partsRequiredId;
      return id != null && id !== "" ? String(id) : null;
    };
    const firstFromRows = (rows) => {
      if (!Array.isArray(rows)) return null;
      for (const row of rows) {
        const id = idFromRow(row);
        if (id) return id;
      }
      return null;
    };

    const fromApi = firstFromRows(bomTable);
    if (fromApi) return fromApi;

    if (!Array.isArray(allNodesData)) return null;
    for (const section of allNodesData) {
      for (const step of section?.steps || []) {
        const pr = step?.detail?.step_data?.parts_required;
        const fromPr = firstFromRows(pr);
        if (fromPr) return fromPr;
        const bomRows = step?.detail?.past_step_data_json?.bom_rows;
        const fromPast = firstFromRows(bomRows);
        if (fromPast) return fromPast;
      }
    }
    return null;
  }, [bomTable, allNodesData]);

  const hideTrainingObstructions = trainingCheckmarkActive;

  /** Live / Products view: do not show edit-lock line ("Locked by …") — confusing on Get/Give Training. */
  const hideLockErrorInLiveMode = isViewMode && !fromAuditTrail;

  /**
   * Per-step tags on the main document view: Get Training when aggregate ≠ "Trained" (hide when only
   * Give Training is offered). Give Training "Checkmark Where Trained" is separate — see below.
   */
  const isGiveTrainingCheckmarkScreen =
    giveTrainingFlow &&
    trainingCheckmarkMode &&
    giveTrainingSelectedTraineeIds.length > 0;

  // If the AppBar is showing "Give Training", hide step status tags.
  // ("Give Training" is shown when the viewer is considered Trained for this revision.)
  const isGiveTrainingButtonPresent =
    isViewMode &&
    !fromAuditTrail &&
    (isRevisionWorkflowActor(currentRevision, authUser?.id) ||
      viewerTrainingStatusWithLetterInheritance === "Trained");

  const showStepTrainingTags =
    isViewMode &&
    !fromAuditTrail &&
    !isGiveTrainingButtonPresent &&
    viewerTrainingStatusWithLetterInheritance != null &&
    viewerTrainingStatusWithLetterInheritance !== "Trained";

  /** Pass `trained_to` step sets into StepNode (tags + checkbox opacity). Main view OR Give Training checkmark — not the plain main view when tags are hidden for Give-only. */
  const passPerStepTrainingSetsToRevisionNodes =
    showStepTrainingTags || isGiveTrainingCheckmarkScreen;

  const currentCompleteTrainingTraineeId =
    completeTrainingTraineeIds?.[completeTrainingTraineeIndex] ?? null;
  const isLastCompleteTrainingTrainee =
    completeTrainingTraineeIndex >=
    (completeTrainingTraineeIds?.length || 0) - 1;

  const currentCompleteTrainingTraineeName = useMemo(() => {
    if (giveTrainingFlow) {
      const idStr =
        currentCompleteTrainingTraineeId == null
          ? null
          : String(currentCompleteTrainingTraineeId);
      const fromSession = giveTrainingSessionTraineeEntries.find(
        (e) => e.id === idStr,
      )?.label;
      if (fromSession) return fromSession;
      return (
        traineeOptionsForGiveTraining.find((o) => o.id === idStr)?.label || "—"
      );
    }
    return traineeDisplayName || "—";
  }, [
    giveTrainingFlow,
    currentCompleteTrainingTraineeId,
    giveTrainingSessionTraineeEntries,
    traineeOptionsForGiveTraining,
    traineeDisplayName,
  ]);

  /** Steps + Procedure-only checkbox rules: Add Module “live” (Table of Procedures) or training checkmark flow — not classic revision routes. */
  const applyRestrictedCheckboxRules =
    (fromAddModule && isViewMode) || hideTrainingObstructions;

  const isProcedurePartType =
    String(currentRevision?.type_name ?? "")
      .trim()
      .toLowerCase() === "procedure";

  /** Live / view mode only: Revisions Table shows Released and Archived rows. Edit mode: full list. */
  const revisionDataForTable = useMemo(() => {
    if (!isViewMode || !Array.isArray(revisionData)) return revisionData;
    return revisionData.filter((rev) => {
      const s = String(getCurrentStatus(rev) || "")
        .trim()
        .toLowerCase();
      return s === "released" || s === "archived";
    });
  }, [isViewMode, revisionData]);

  return (
    <div style={{ paddingBottom: "20px" }}>
      {/* Document App Bar - Handles document operations and modals */}
      <DocumentAppBar
        type={!fromAuditTrail ? currentRevision?.type_name : cancel_screen_data?.item_code_type}
        partNumber={!fromAuditTrail ? currentRevision?.part_number : cancel_screen_data?.partNumber}
        revision={!fromAuditTrail ? currentRevision?.rev : cancel_screen_data?.revision}
        revisionId={revisionId}
        partId={partId}
        description={!fromAuditTrail ? currentRevision?.part_description : cancel_screen_data?.partDescription}
        revisionData={revisionData}
        trainingLogs={trainingLogs}
        trainingStatusRefreshKey={trainingStatusRefreshKey}
        bomTable={bomTableData}
        currentRevision={currentRevision}
        currentStatus={!fromAuditTrail ? currentStatus : cancel_screen_data?.status}
        allNodesData={allNodesData}
        lockStatus={lockStatus}
        onSubmitSuccess={handleDocumentRefresh}
        onRejectSuccess={handleDocumentRefresh}
        onOpenVersionHistory={() => setShowVersionHistory(true)}
        onCloseAdditionalInfo={() => setShowAdditionalInfo(false)}
        disabled={documentAppBarDisabled}
        previewDisabled={false}
        previewDisabledTitle=""
        showTablesDropdown={!fromAuditTrail && !isViewMode}
        showBookmark={!fromAuditTrail}
        onBookmarkClick={() => {
          const nextIsViewMode = !isViewMode;
          if (nextIsViewMode && !isReleasedRevision) {
            navigate("/404");
            return;
          }
          const v = String(nextIsViewMode);
          if (revisionId) {
            sessionStorage.setItem(`document_is_view_mode_${revisionId}`, v);
          }
          setGlobalAppViewMode(nextIsViewMode);
          navigate(
            {
              pathname: ".",
              search: location.search,
              hash: location.hash,
            },
            {
              replace: true,
              state: {
                ...(location.state || {}),
                isViewMode: nextIsViewMode,
                fromLiveMode: !nextIsViewMode,
                fromViewMode: nextIsViewMode,
              },
            },
          );
        }}
        showProfileButton={!fromAuditTrail}
        fromAuditTrail={fromAuditTrail}
        auditTrailSavedAtIso={auditTrailSavedAtIso}
        isViewMode={isViewMode}
        onGetTrainingClick={() => {
          setShowSelectTraineePopup(false);
          setGiveTrainingFlow(false);
          setGetTrainingSessionTrainerId("");
          setGetTrainingSessionTrainerLabel("");
          setGiveTrainingSessionTraineeEntries([]);
          setShowSelectTrainerPopup(true);
        }}
        onGiveTrainingClick={() => {
          setShowSelectTrainerPopup(false);
          setGetTrainingSessionTrainerId("");
          setGetTrainingSessionTrainerLabel("");
          setGiveTrainingSessionTraineeEntries([]);
          setGiveTrainingSelectedTraineeIds([]);
          dispatch(clearGiveTrainingTraineeUsers());
          setShowSelectTraineePopup(true);
        }}
        trainingCheckmarkMode={
          isViewMode && !fromAuditTrail && trainingCheckmarkMode
        }
        trainerDisplayName={trainingBarTrainerDisplayName}
        traineeDisplayName={traineeDisplayName}
        onTrainingCheckmarkCancel={() => {
          setTrainingCheckmarkMode(false);
          setGetTrainingSessionTrainerId("");
          setGetTrainingSessionTrainerLabel("");
          setGiveTrainingSessionTraineeEntries([]);
        }}
        onTrainingCheckmarkNext={handleTrainingCheckmarkNextClick}

        cancel_screen_data={location?.state?.cancel_screen_data}
      />

      {showCompleteTrainingModal && (
        <CompleteTrainingModal
          key={`complete-training-${currentCompleteTrainingTraineeId || "single"}-${completeTrainingTraineeIndex}`}
          closeModal={() => {
            setShowCompleteTrainingModal(false);
            setTrainingCheckmarkMode(false);
            setGetTrainingSessionTrainerId("");
            setGetTrainingSessionTrainerLabel("");
            setGiveTrainingSessionTraineeEntries([]);
          }}
          closeOnSuccess={isLastCompleteTrainingTrainee}
          confirmLabel={
            isLastCompleteTrainingTrainee
              ? giveTrainingFlow
                ? "Finish"
                : "Confirm"
              : "Next"
          }
          partNumber={
            !fromAuditTrail
              ? currentRevision?.part_number
              : cancel_screen_data?.partNumber
          }
          revision={currentRevision?.rev}
          trainerName={trainingBarTrainerDisplayName}
          traineeName={currentCompleteTrainingTraineeName}
          onConfirm={handleCompleteTrainingConfirm}
        />
      )}

      {/* Document Header - Displays version info and status */}
      <DocumentHeader
        type={!fromAuditTrail ? currentRevision?.type_name : cancel_screen_data?.item_code_type}
        partNumber={!fromAuditTrail ? currentRevision?.part_number : cancel_screen_data?.partNumber}
        revision={!fromAuditTrail ? currentRevision?.rev : cancel_screen_data?.revision}
        revisionId={revisionId}
        partId={partId}
        description={!fromAuditTrail ? currentRevision?.part_description : cancel_screen_data?.partDescription}
        status={!fromAuditTrail ?  currentStatus : cancel_screen_data?.status}
        currentRevision={currentRevision}
        revisionData={revisionData}
        isCheckboxEnabled={isCheckboxEnabled}
        showCheckbox={
          applyRestrictedCheckboxRules
            ? hideTrainingObstructions
              ? true
              : isProcedurePartType && !isViewMode
            : !isViewMode
        }
        onCheckboxClick={
          hideTrainingObstructions
            ? handleTrainingProcedureHeaderClick
            : handleDocumentHeaderCheckboxClick
        }
        {...(hideTrainingObstructions
          ? {
              isCheckboxChecked:
                trainingCheckmarkSelection?.scope === "procedure",
            }
          : {})}
        showAdditionalInfo={showAdditionalInfo}
        onShowAdditionalInfoChange={setShowAdditionalInfo}
        lockErrorMessage={
          fromAuditTrail || hideLockErrorInLiveMode ? null : lockErrorMessage
        }
        useGreyStatusColor={fromAuditTrail}
        hideRevisionReleaseHistory={hideTrainingObstructions}
        fromAuditTrail={fromAuditTrail}
      />

      {!hideTrainingObstructions && (
        <>
          {/* Revisions Table — collapsible only in view/live mode; always expanded in normal edit */}
          <div style={revisionsTableSectionBlockStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  ...collapsibleSectionTitleRowStyle,
                }}
              >
                <h3
                  style={{
                    ...collapsibleSectionHeadingStyle,
                    marginTop: 0,
                  }}
                >
                  Revisions Table
                </h3>
                {isViewMode &&
                  renderSectionToggle(showRevisionTableSection, () =>
                    setShowRevisionTableSection((prev) => !prev),
                  )}
                {isViewMode && !showRevisionTableSection && (
                  <p style={collapsedSectionHintStyle}>
                    Revisions Table is Collapsed. To View, Click Button to
                    Expand.
                  </p>
                )}
              </div>
              {showCreateRevisionButton && !fromAuditTrail && !isViewMode && (
                <div className="button-container" style={{ margin: 0 }}>
                  <button
                    className="create-revision-button"
                    onClick={() => {
                      if (!isCreateButtonDisabled && revisionTableRef.current) {
                        // Call RevisionTable's create revision function directly
                        revisionTableRef.current.onCreateRevision();
                      }
                    }}
                    disabled={isCreateButtonDisabled}
                    style={{
                      opacity: isCreateButtonDisabled ? 0.5 : 1,
                      cursor: isCreateButtonDisabled
                        ? "not-allowed"
                        : "pointer",
                    }}
                    title={
                      isCreateButtonDisabled
                        ? !canCreate
                          ? "You do not have permission to create revisions"
                          : "Revision is locked by another user"
                        : undefined
                    }
                  >
                    Create New Revision
                  </button>
                </div>
              )}
            </div>
            {loading.revisionData ? (
              <div style={{ marginTop: "14px" }}>
                <InlineLoader text="Loading revisions..." height="300px" />
              </div>
            ) : !isViewMode || showRevisionTableSection ? (
              <RevisionTable
                ref={revisionTableRef}
                revisionData={revisionDataForTable}
                tableMarginTop="10px"
                revisionId={revisionId}
                showCreateButton={
                  showCreateRevisionButton && !fromAuditTrail && !isViewMode
                }
                isCreateButtonDisabled={isCreateButtonDisabled}
                canCreate={canCreate}
                isReadOnly={isRevisionTableReadOnly || fromAuditTrail}
                // hideLinkStyle={fromAuditTrail}
                partId={partId}
                partNumber={currentRevision?.part_number}
                type={currentRevision?.type_name}
                description={currentRevision?.part_description}
                myRevision={currentRevision?.rev}
                users={users}
                approvedUsers={approvedUsers}
                releasedUsers={releasedUsers}
                rule={rule}
                onRevisionCreated={handleDocumentRefresh}
                onCloseAdditionalInfo={() => setShowAdditionalInfo(false)}
                showHeading={false}
              />
            ) : null}
          </div>

          {/* Training Log — collapsible only in view/live mode; always expanded in normal edit */}
          <div style={trainingLogSectionBlockStyle}>
            <div style={collapsibleSectionTitleRowStyle}>
              <h3
                style={{
                  ...collapsibleSectionHeadingStyle,
                  marginTop: 0,
                }}
              >
                Training Log
              </h3>
              {isViewMode &&
                renderSectionToggle(showTrainingLogSection, () =>
                  setShowTrainingLogSection((prev) => !prev),
                )}
              {isViewMode && !showTrainingLogSection && (
                <p style={collapsedSectionHintStyle}>
                  Training Log Table is Collapsed. To View, Click Button to
                  Expand.
                </p>
              )}
            </div>
            {(!isViewMode || showTrainingLogSection) && (
              <TrainingLogTable
                trainingLogs={!fromAuditTrail ? trainingLogs : cancel_screen_data?.present_training_logs}
                showHeading={false}
                allNodesData={allNodesData}
                procedureRevisionId={revisionId}
                currentRev={currentRevision?.rev}
                revisionData={revisionData}
              />
            )}
          </div>

          {/* BOM Table - Bill of Materials */}
          <BOMTable
            type={!fromAuditTrail ? currentRevision?.type_name: cancel_screen_data?.item_code_type }
            bomTable={bomTableData}
            showAdjustQtyButton={isViewMode}
            onAdjustQtyClick={() => {
              setAdjustQtyValue("");
              setShowAdjustQtyPopup(true);
            }}
            totalQtyToBuild={totalQtyToBuild}
          />
        </>
      )}

      {/* Revision Nodes - Hierarchical section/step/substep structure */}
      {loading.revisionData ? (
        <InlineLoader text="Loading revision nodes..." height="300px" />
      ) : (
        <RenderRevisionNodes
          preloadedData={fromAuditTrail ? cancel_screen_data?.data : null}
          partRef={partId}
          procedureRevRef={revisionId}
          type={
            !fromAuditTrail
              ? currentRevision?.type_name
              : cancel_screen_data?.item_code_type
          }
          partNumber={currentRevision?.part_number}
          revision={currentRevision?.rev}
          disabled={shouldDisableNodesCheckboxes}
          showCheckbox={
            applyRestrictedCheckboxRules
              ? !fromAuditTrail && (!isViewMode || hideTrainingObstructions)
              : !fromAuditTrail && !isViewMode
          }
          {...(applyRestrictedCheckboxRules
            ? {
                showSectionCheckbox: false,
                hideChildCheckboxes: true,
                hideSubstepCheckboxes: true,
              }
            : {})}
          onDataLoaded={setAllNodesData}
          refreshKey={nodesRefreshKey}
          isLock={isLock}
          isRevisionLocked={isRevisionLocked}
          isLeader={bypassTabOrderLock || isFirstTabByOrder}
          current_snapshot={
            currentRevision?.current_snapshot
              ? currentRevision?.current_snapshot
              : null
          }
          hideChildHeadingsForTypes={
            isViewMode
              ? HIDE_CHILD_HEADING_TYPES_TRAINING_CHECKMARK
              : null
          }
          customOnNavigate={
            hideTrainingObstructions
              ? handleTrainingCheckmarkStepNavigate
              : undefined
          }
          checkboxState={
            hideTrainingObstructions
              ? trainingRevisionNodesCheckboxState
              : undefined
          }
          trainedStepNumbers={
            passPerStepTrainingSetsToRevisionNodes
              ? trainedStepNumbersSet
              : null
          }
          partiallyTrainedStepNumbers={
            passPerStepTrainingSetsToRevisionNodes
              ? partiallyTrainedStepNumbersSet
              : null
          }
          allStepsTrained={
            passPerStepTrainingSetsToRevisionNodes ? allStepsTrained : false
          }
          totalQtyToBuild={totalQtyToBuild}
          auditTrailNewContentSnapshotUrl={auditTrailNewContentSnapshotUrl}
          main_entry={location?.state?.main_entry}
          enableCanvasStagePopup={true}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          title={versionHistoryPopupTitle}
          versionHistories={
            allVersionHistories.length > 0
              ? allVersionHistories
              : versionHistories
          }
          mode="edit"
          initialFilterType="all"
          historyFilterDropdownDisabled
          breadcrumbText={versionHistoryBreadcrumbFallback}
          revisionTreeNodes={allNodesData}
          procedureRevisionIdForNodeHistory={revisionId}
          restrictNodeHistoryToProcedureRevision={allVersionHistories.length === 0}
          viewActionDisabled={!canClick}
          onViewDetails={(row) => {
            const auditId = row.audit_trail_id || row.audit_trail_id_ref;
            const path = getVersionHistoryAuditPreviewPath(
              auditId,
              row.action,
            );
            if (path) {
              navigate(path);
            } else {
              alert("No preview available for this version");
            }
          }}
          exportCsvDisabled={
            loading.revisionData ||
            allNodesData == null ||
            versionHistoryTableLoading
          }
          exportCsvButtonTitle="Export will be enabled once all nodes are loaded."
        />
      )}

      {showAdjustQtyPopup && (
        <div className="revision-modal-overlay">
          <div className="revision-modal-content adjust-qty-modal">
            <div className="revision-modal-header">
              <h3 className="revision-modal-title adjust-qty-modal-title">
                Adjust Qty
              </h3>
              <button
                type="button"
                onClick={() => {
                  setAdjustQtySubmitting(false);
                  setShowAdjustQtyPopup(false);
                }}
                className="revision-close-button"
                title="Close"
                aria-label="Close modal"
              >
                <FontAwesomeIcon icon={faTimes} size="1x" color="#6c757d" />
              </button>
            </div>

            <hr className="divider" />

            <div className="adjust-qty-modal-body">
              <div className="adjust-qty-modal-form-row">
                <label
                  htmlFor="adjust-qty-input"
                  className="adjust-qty-modal-label"
                >
                  Enter Qty
                </label>
                <input
                  id="adjust-qty-input"
                  type="text"
                  inputMode="numeric"
                  className="adjust-qty-modal-input"
                  value={adjustQtyValue}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    if (/^\d*\.?\d*$/.test(nextValue)) {
                      setAdjustQtyValue(nextValue);
                    }
                  }}
                  placeholder="Enter quantity"
                  disabled={adjustQtySubmitting}
                />
              </div>
            </div>

            <hr className="divider" />

            <div className="adjust-qty-modal-footer">
              <ModalActions
                onClose={() => {
                  setAdjustQtySubmitting(false);
                  setShowAdjustQtyPopup(false);
                }}
                onSubmit={async () => {
                  const parsedQty = Number(adjustQtyValue);
                  if (!Number.isFinite(parsedQty) || parsedQty < 0) {
                    dispatch(
                      showSnackbar({
                        type: "error",
                        message: "Please enter a valid quantity.",
                      }),
                    );
                    return;
                  }
                  // Live mode should not call the backend (no update-qty). Keep this local-only.
                  setTotalQtyToBuild(parsedQty);
                  try {
                    if (totalQtyToBuildStorageKey) {
                      localStorage.setItem(
                        totalQtyToBuildStorageKey,
                        String(parsedQty),
                      );
                    }
                  } catch {
                    /* ignore */
                  }
                  setShowAdjustQtyPopup(false);
                  dispatch(
                    showSnackbar({
                      type: "success",
                      message: "Quantity updated.",
                    }),
                  );
                }}
                submitLabel="Confirm"
                closeLabel="Close"
                loading={adjustQtySubmitting}
                loadingText="Saving..."
                disabled={adjustQtySubmitting}
              />
            </div>
          </div>
        </div>
      )}

      {isViewMode && showSelectTraineePopup && (
        <SelectTraineesModal
          onClose={() => {
            dispatch(clearGiveTrainingTraineeUsers());
            setShowSelectTraineePopup(false);
          }}
          loading={giveTrainingTraineeUsersLoading}
          traineeOptions={traineeOptionsForGiveTraining}
          selectedTraineeIds={giveTrainingSelectedTraineeIds}
          onToggleTrainee={handleToggleGiveTrainee}
          onNext={handleSelectTraineeNext}
        />
      )}

      {isViewMode && showSelectTrainerPopup && (
        <div className="revision-modal-overlay">
          <div
            className="revision-modal-content select-trainer-modal"
            style={
              !selectTrainerModalBusy &&
              partRefForSelectTrainer &&
              selectTrainerModalOptions.length > 0
                ? { "--trainer-column-count": selectTrainerColumnCount }
                : undefined
            }
          >
            <div className="revision-modal-header">
              <h3 className="revision-modal-title select-trainer-modal-title">
                Select the Trainer
              </h3>
              <button
                type="button"
                onClick={() => setShowSelectTrainerPopup(false)}
                className="revision-close-button"
                title="Close"
                aria-label="Close modal"
              >
                <FontAwesomeIcon icon={faTimes} size="1x" color="#6c757d" />
              </button>
            </div>

            <hr className="divider" />

            <div className="select-trainer-body">
              <div className="select-trainer-label">Trainer:</div>
              <div className="select-trainer-options">
                {selectTrainerModalBusy ? (
                  <InlineLoader />
                ) : !partRefForSelectTrainer ? (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#6c757d",
                    }}
                  >
                    Part context is required to load trainers.
                  </p>
                ) : selectTrainerModalOptions.length === 0 ? (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#6c757d",
                    }}
                  >
                    No matching users.
                  </p>
                ) : (
                  <div className="select-trainer-columns">
                    {selectTrainerModalColumns.map((col, colIdx) => (
                      <div
                        key={`trainer-col-${colIdx}`}
                        className="select-trainer-column"
                      >
                        {col.map((trainer) => (
                          <label
                            key={trainer.id}
                            className="select-trainer-option"
                          >
                            <input
                              type="radio"
                              name="trainer-selection"
                              value={trainer.id}
                              checked={selectedTrainer === trainer.id}
                              onChange={(e) =>
                                setSelectedTrainer(e.target.value)
                              }
                            />
                            <span className="select-trainer-name">
                              {trainer.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <hr className="divider" />

            <div className="select-trainer-footer">
              <ModalActions
                onClose={() => setShowSelectTrainerPopup(false)}
                onSubmit={() => {
                  if (
                    selectTrainerModalOptions.length > 0 &&
                    !selectedTrainer
                  ) {
                    dispatch(
                      showSnackbar({
                        type: "error",
                        message: "Please select a trainer.",
                      }),
                    );
                    return;
                  }
                  const pickedLabel =
                    selectTrainerModalOptions.find(
                      (o) => o.id === selectedTrainer,
                    )?.label ?? "";
                  setGetTrainingSessionTrainerId(
                    selectedTrainer ? String(selectedTrainer).trim() : "",
                  );
                  setGetTrainingSessionTrainerLabel(
                    typeof pickedLabel === "string" ? pickedLabel.trim() : "",
                  );
                  setGiveTrainingFlow(false);
                  setShowSelectTrainerPopup(false);
                  setTrainingCheckmarkMode(true);
                }}
                submitLabel="Next"
                closeLabel="Close"
                loading={selectTrainerModalBusy}
                loadingText="Loading..."
                disabled={selectTrainerModalBusy}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Document;
